import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, LineChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, Cell, ComposedChart, Line, ScatterChart, Scatter, LabelList,
} from "recharts";
import { useWell } from "../WellContext";
import { useAIAdvice } from "../useAIAdvice";
import { useTheme, getColors } from "../ThemeContext";
import PlotSummary from "../components/PlotSummary";
const C = {
  bg: "#050b14", panel: "#080f1e", card: "#0b1525",
  border: "#0f2040", border2: "#162a4a",
  cyan: "#00d4ff", teal: "#00ffcc", amber: "#ffb84d",
  pink: "#afeeee", purple: "#8b7eff", blue: "#3b9eff",
  darkCyan: "#0b647d",
  red: "#ff3d5a", green: "#00cc99", orange: "#ff7c2a", lime: "#b0ff4a",
  text: "#e2eaf8", muted: "#4a6080", muted2: "#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
const EPA_API = "/api/epa";
/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}


/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


/* EPA windows server-side: the DATA WINDOW bar is turned into query params every EPA endpoint understands (no params = unchanged) */
function epaWinQs(win, tMax) {
  if (win.mode === "all" || tMax == null) return "";
  if (win.mode === "cyc30") return "last=30";               // newest 30 cycles of EACH well
  if (win.mode === "custom") {
    const p = [];
    if (win.from) p.push(`from=${encodeURIComponent(win.from + " 00:00")}`);
    if (win.to)   p.push(`to=${encodeURIComponent(win.to + " 23:59")}`);
    return p.join("&");
  }
  const pr = PRESETS.find(x => x.id === win.mode);
  return `from=${encodeURIComponent(`${toInput(tMax - pr.ms)} ${fmtHM(tMax - pr.ms)}`)}&to=${encodeURIComponent(`${toInput(tMax)} ${fmtHM(tMax)}`)}`;
}
const withQs = (url, qs) => qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
const WELL_COLORS = [
  C.cyan, C.teal, C.purple, C.darkCyan, C.pink, C.blue,
  C.orange, C.lime, "#e879f9", "#34d399", "#f97316", "#60a5fa",
];

const EPA = {
  pass: "#00cc99", marginal: "#ffb84d", fail: "#ff3d5a",
  risk: "#ff7c2a", emit: "#8b7eff", score: "#00d4ff",
};

const TIER_DEFS = [
  { code: "FC",  full: "Fully Compliant", min: 85, color: "#00cc99" },
  { code: "C",   full: "Compliant",       min: 70, color: "#00ffcc" },
  { code: "M",   full: "Marginal",        min: 55, color: "#ffb84d" },
  { code: "AR",  full: "At Risk",         min: 40, color: "#ff7c2a" },
  { code: "NC",  full: "Non-Compliant",   min:  0, color: "#ff3d5a" },
];

function tierColor(tier) {
  const t = TIER_DEFS.find(d => d.full === tier || d.code === tier);
  return t ? t.color : C.muted;
}
function tierColorFromScore(score) {
  for (const d of TIER_DEFS) { if ((score ?? 0) >= d.min) return d.color; }
  return C.red;
}

/* Hover status that matches the bar colour: teal/green = good, amber/orange = warning, red = critical */
function tierStatusFromScore(score) {
  if (typeof score !== "number") return undefined;
  if (score >= 85) return "EXCELLENT (Fully Compliant)";
  if (score >= 70) return "GOOD (Compliant)";
  if (score >= 55) return "WARNING (Marginal: amber)";
  if (score >= 40) return "WARNING (At Risk)";
  return "CRITICAL (Non-Compliant)";
}

const TIER_LINES = TIER_DEFS.slice(0, 4).map(d => ({ y: d.min, color: d.color, label: d.code }));

function TierLegend({ compact = false }) {
  return (
    <div style={{
      display: "flex", gap: compact ? 8 : 12, flexWrap: "wrap",
      alignItems: "center",
    }}>
      {TIER_DEFS.map(d => (
        <div key={d.code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{
            fontFamily: MONO, fontSize: 8, fontWeight: 700,
            color: d.color, background: `${d.color}18`,
            border: `1px solid ${d.color}44`, borderRadius: 3,
            padding: "1px 5px", letterSpacing: 0.5,
          }}>{d.code}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>
            {d.full} {!compact && `(≥${d.min})`}
          </span>
        </div>
      ))}
    </div>
  );
}

const THRESHOLD_META = {
  velocity_compliance_min: { label: "Velocity Min: EPA NSPS",   unit: "ft/min", source: "EPA NSPS OOOO/OOOOa" },
  velocity_compliance_max: { label: "Velocity Max: EPA NSPS",   unit: "ft/min", source: "EPA NSPS OOOO/OOOOa" },
  velocity_optimal_min:    { label: "Velocity Optimal Min: WP", unit: "ft/min" },
  velocity_optimal_max:    { label: "Velocity Optimal Max: WP", unit: "ft/min" },
  load_factor_fail:        { label: "Load Factor Fail (Lf ≥)",   unit: "" },
  load_factor_warn:        { label: "Load Factor Warn (Lf ≥)",   unit: "",      source: "Operational buffer" },
  shutin_eff_min:          { label: "Shut-In Efficiency Min",     unit: "×" },
  vent_threshold_min:      { label: "Vent Threshold (dynamic)",   unit: "min",   source: "25th pct of +VentMin" },
};

function flagColor(f) {
  return ({
    OVERLOAD:     C.red,
    VEL_HIGH:     C.red,
    VEL_LOW:      C.orange,
    SHUTIN_SHORT: C.pink,
    VENTING:      C.purple,
    LOAD_WARN:    C.amber,
    VEL_SUBOPT:   C.blue,
  })[f] || C.muted;
}

const FLAG_DESC = {
  OVERLOAD:     "Lf ≥ 0.50, plunger cannot reach surface",
  VEL_HIGH:     "v > 1,200 ft/min, equipment damage risk",
  VEL_LOW:      "v < 350 ft/min, liquid unloading fails",
  SHUTIN_SHORT: "ShutInEff < 1.0, insufficient pressure build-up",
  VENTING:      "VentMin > threshold, methane emissions event",
  LOAD_WARN:    "0.40 ≤ Lf < 0.50, approaching overload",
  VEL_SUBOPT:   "350-500 ft/min, below optimal range (WP §4.6)",
};

const FLAG_FULL_NAMES = {
  OVERLOAD:     "Overload",
  VEL_HIGH:     "Velocity Too High",
  VEL_LOW:      "Velocity Too Low",
  SHUTIN_SHORT: "Shut-In Too Short",
  VENTING:      "Venting Event",
  LOAD_WARN:    "Load Warning",
  VEL_SUBOPT:   "Velocity Suboptimal",
};

function riskColor(r)   { return ({ Low: C.green, Medium: C.amber, High: C.red })[r] || C.muted; }
function statusColor(s) { return ({ pass: EPA.pass, marginal: EPA.marginal, fail: EPA.fail })[s] || C.muted; }

function TierBadge({ tier, short, score, size = "small" }) {
  const color = tier ? tierColor(tier) : tierColorFromScore(score ?? 0);
  const code  = short || tier || "-";
  const fullName = TIER_DEFS.find(d => d.code === code || d.full === code)?.full || code;
  return (
    <span title={code} style={{
      fontFamily: MONO, fontWeight: 700,
      fontSize: size === "large" ? 10 : 8,
      letterSpacing: 0.5, textTransform: "uppercase",
      color, background: `${color}18`,
      border: `1.5px solid ${color}55`,
      borderRadius: 4,
      padding: size === "large" ? "3px 10px" : "2px 6px",
      whiteSpace: "nowrap", cursor: "help",
    }}>
      {fullName}
    </span>
  );
}

function Icon({ name, size = 14, color = "currentColor", strokeWidth = 1.8 }) {
  const s = { width: size, height: size, flexShrink: 0 };
  const p = { fill: "none", stroke: color, strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "shield":        return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case "clipboard":     return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect {...p} x="8" y="2" width="8" height="4" rx="1"/></svg>;
    case "search":        return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="11" cy="11" r="8"/><line {...p} x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
    case "wind":          return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>;
    case "award":         return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="12" cy="8" r="6"/><path {...p} d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>;
    case "bar-chart":     return <svg viewBox="0 0 24 24" style={s}><line {...p} x1="18" y1="20" x2="18" y2="10"/><line {...p} x1="12" y1="20" x2="12" y2="4"/><line {...p} x1="6" y1="20" x2="6" y2="14"/></svg>;
    case "globe":         return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="12" cy="12" r="10"/><line {...p} x1="2" y1="12" x2="22" y2="12"/><path {...p} d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
    case "calendar":      return <svg viewBox="0 0 24 24" style={s}><rect {...p} x="3" y="4" width="18" height="18" rx="2"/><line {...p} x1="16" y1="2" x2="16" y2="6"/><line {...p} x1="8" y1="2" x2="8" y2="6"/><line {...p} x1="3" y1="10" x2="21" y2="10"/></svg>;
    case "download":      return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline {...p} points="7 10 12 15 17 10"/><line {...p} x1="12" y1="15" x2="12" y2="3"/></svg>;
    case "cloud":         return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>;
    case "check":         return <svg viewBox="0 0 24 24" style={s}><polyline {...p} points="20 6 9 17 4 12"/></svg>;
    case "check-circle":  return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline {...p} points="22 4 12 14.01 9 11.01"/></svg>;
    case "alert-triangle":return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line {...p} x1="12" y1="9" x2="12" y2="13"/><line {...p} x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    case "alert-circle":  return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="12" cy="12" r="10"/><line {...p} x1="12" y1="8" x2="12" y2="12"/><line {...p} x1="12" y1="16" x2="12.01" y2="16"/></svg>;
    case "refresh":       return <svg viewBox="0 0 24 24" style={s}><polyline {...p} points="23 4 23 10 17 10"/><polyline {...p} points="1 20 1 14 7 14"/><path {...p} d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
    case "trending-up":   return <svg viewBox="0 0 24 24" style={s}><polyline {...p} points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline {...p} points="17 6 23 6 23 12"/></svg>;
    case "trending-down": return <svg viewBox="0 0 24 24" style={s}><polyline {...p} points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline {...p} points="17 18 23 18 23 12"/></svg>;
    case "leaf":          return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M2 22 16 8M16.5 4.5C20 4.5 19.5 9 16.5 9H13C13 13.5 11 17.5 8 19"/></svg>;
    case "tool":          return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
    case "calculator":    return <svg viewBox="0 0 24 24" style={s}><rect {...p} x="4" y="2" width="16" height="20" rx="2"/><line {...p} x1="8" y1="6" x2="16" y2="6"/><line {...p} x1="8" y1="10" x2="8" y2="10"/><line {...p} x1="12" y1="10" x2="12" y2="10"/><line {...p} x1="16" y1="10" x2="16" y2="10"/><line {...p} x1="8" y1="14" x2="8" y2="14"/><line {...p} x1="12" y1="14" x2="12" y2="14"/><line {...p} x1="16" y1="14" x2="16" y2="14"/><line {...p} x1="8" y1="18" x2="12" y2="18"/><line {...p} x1="16" y1="18" x2="16" y2="18"/></svg>;
    case "activity":      return <svg viewBox="0 0 24 24" style={s}><polyline {...p} points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
    case "clock":         return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="12" cy="12" r="10"/><polyline {...p} points="12 6 12 12 16 14"/></svg>;
    case "x":             return <svg viewBox="0 0 24 24" style={s}><line {...p} x1="18" y1="6" x2="6" y2="18"/><line {...p} x1="6" y1="6" x2="18" y2="18"/></svg>;
    case "bot":           return <svg viewBox="0 0 24 24" style={s}><rect {...p} x="3" y="11" width="18" height="10" rx="2"/><circle {...p} cx="12" cy="5" r="2"/><path {...p} d="M12 7v4"/><line {...p} x1="8" y1="16" x2="8" y2="16"/><line {...p} x1="16" y1="16" x2="16" y2="16"/></svg>;
    case "pin":           return <svg viewBox="0 0 24 24" style={s}><path {...p} d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle {...p} cx="12" cy="10" r="3"/></svg>;
    default:              return <svg viewBox="0 0 24 24" style={s}><circle {...p} cx="12" cy="12" r="10"/></svg>;
  }
}

function fmt(v, d = 1) {
  if (v == null || isNaN(Number(v))) return "-";
  return Number(v).toFixed(d);
}
function fmtK(v) {
  if (v == null || isNaN(Number(v))) return "-";
  const n = Number(v);
  return Math.abs(n) >= 1000 ? (n / 1000).toFixed(2) + "k" : n.toFixed(2);
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: `2px solid ${C.border2}`, borderTopColor: C.cyan,
        animation: "spin-slow 0.9s linear infinite",
      }} />
    </div>
  );
}

function SectionTitle({ children, sub, accent = C.cyan }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", color: accent }}>{children}</div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Card({ children, style = {}, accentTop }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: "16px 18px", position: "relative", overflow: "hidden",
      ...(accentTop ? { borderTop: `3px solid ${accentTop}` } : {}),
      ...style,
    }}>
      {accentTop && (
        <div style={{ position: "absolute", top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(to right, transparent, ${accentTop}66, transparent)` }} />
      )}
      {children}
    </div>
  );
}

function KpiCard({ label, value, unit = "", color = C.cyan, sub, icon, badge }) {
  return (
    <Card accentTop={color} style={{ minWidth: 0 }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 70, height: 70, borderRadius: "50%", background: `${color}0d`, pointerEvents: "none" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <Icon name={icon} size={16} color={color} />
        {badge && (
          <span style={{ fontFamily: MONO, fontSize: 8, color, background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 3, padding: "2px 6px", letterSpacing: 1, textTransform: "uppercase" }}>{badge}</span>
        )}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, color, lineHeight: 1 }}>
        {value}<span style={{ fontSize: 11, marginLeft: 3, color: `${color}99` }}>{unit}</span>
      </div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 5 }}>{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, sub, children, height = 240, accent = C.cyan, summaryTitle, summarySubtitle, summaryChart, lensId, footer }) {
  return (
    <Card>
      <div style={{ position: "absolute", top: 0, left: 14, right: 14, height: 2, background: `linear-gradient(to right, transparent, ${accent}88, transparent)` }} />
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text }}>{title}</div>
        {sub && <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ height }}>{children}</div>
      {footer && <div style={{ marginTop: 8 }}>{footer}</div>}
      <div style={{ marginTop: 10 }}>
        <PlotSummary inline lensId={lensId} title={summaryTitle || title} subtitle={summarySubtitle || (typeof sub === "string" ? sub : "")} accent={accent}>{summaryChart || children}</PlotSummary>
      </div>
    </Card>
  );
}

const TTIP_STYLE = {
  background: C.card, border: `1px solid ${C.border2}`, borderRadius: 8,
  padding: "8px 12px", fontFamily: MONO, fontSize: 10,
  boxShadow: "0 12px 40px rgba(0,0,0,0.8)",
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={TTIP_STYLE}>
      <div style={{ color: C.muted, marginBottom: 5, fontSize: 9 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: 2 }}>
          <span style={{ color: p.color || C.cyan }}>{p.name}</span>
          <span style={{ color: C.text, fontWeight: 700 }}>{typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function _epaAdviceSeverity(status) {
  if (!status) return 0;
  if (status.startsWith("CRITICAL")) return 3;
  if (status.startsWith("WARNING"))  return 2;
  if (status.startsWith("EXCELLENT") || status.startsWith("GOOD")) return 1;
  return 0;
}

/* Hover-card timing (ms): wait before asking the AI so quick sweeps across points don't flood it, and keep the card up briefly when the cursor crosses a gap between bars/points */
const EPA_ADVICE_DEBOUNCE_MS = 350;
const EPA_CARD_HIDE_DELAY_MS = 150;

/* Latest cursor position, tracked once for the whole page (a plain variable, so moving the mouse never re-renders anything) */
let _epaMouse = null;
let _epaMouseBound = false;
function _epaTrackMouse() {
  if (_epaMouseBound || typeof window === "undefined") return;
  _epaMouseBound = true;
  window.addEventListener("mousemove", (e) => { _epaMouse = { x: e.clientX, y: e.clientY }; }, { passive: true });
}

function EPAHoverAdvice({ active, payload, label, fieldKey, contextData = {}, resolveStatus, labelFormatter, syncAdviceTone = false }) {
  _epaTrackMouse();
  const { advice, loading, fetchAdvice } = useAIAdvice();
  const anchorRef     = useRef(null);      // where the card sits: fixed at the cursor spot where the hovered point became active
  const anchorSigRef  = useRef(null);
  const lastHoverRef  = useRef(null);      // last real hover data, shown during the short hide delay
  const [visible, setVisible]     = useState(false);
  const [settledSig, setSettledSig] = useState(null);

  const hasData = !!(active && payload?.length);
  if (hasData) lastHoverRef.current = { payload, label };
  const cur = lastHoverRef.current;
  const curPayload = cur?.payload;
  const curLabel   = cur?.label;

  const displayLabel = typeof labelFormatter === "function" ? labelFormatter(curLabel) : curLabel;

  /* show immediately, hide only after a short delay so the card does not blink between neighbouring bars/points */
  useEffect(() => {
    if (hasData) { setVisible(true); return undefined; }
    const t = setTimeout(() => setVisible(false), EPA_CARD_HIDE_DELAY_MS);
    return () => clearTimeout(t);
  }, [hasData]);

  /* what is currently hovered, worked out once per render */
  let sig = null, fetchArgs = null;
  if (hasData) {
    const allFields = payload
      .filter(p => p.value != null && typeof p.value === "number")
      .map(p => {
        const fld = p.dataKey || p.name || fieldKey || "";
        const status = typeof resolveStatus === "function" ? resolveStatus(fld, p.value, p.payload) : undefined;
        return {
          field: fld,
          label: p.name || p.dataKey || fieldKey || "",
          value: Math.round(p.value * 100) / 100,
          unit: p.unit || "",
          ...(status ? { status } : {}),
        };
      });
    const severePick = allFields.reduce((best, f) => {
      if (!f.status) return best;
      return (!best || _epaAdviceSeverity(f.status) >= _epaAdviceSeverity(best.status)) ? f : best;
    }, null);

    const primaryP = payload[0];
    const val = severePick ? severePick.value : (typeof primaryP.value === "number" ? primaryP.value : null);
    if (val != null) {
      const primaryField = severePick ? severePick.field : (fieldKey || primaryP.dataKey);
      const primaryUnit  = severePick ? severePick.unit  : (primaryP.unit || "");
      const allValsKey = allFields.map(f => `${f.field}:${f.value}:${f.status || ""}`).join("|");
      sig = `${primaryField}_${Math.round(val * 10) / 10}_${label}_${allValsKey}`;
      const context = { ...contextData };
      allFields.forEach(f => { context[f.field] = f.value; });
      fetchArgs = [primaryField, val, primaryUnit, label, context, allFields];
    }
  }

  /* ask the AI once the cursor has rested on a point for a moment */
  useEffect(() => {
    if (!sig || !fetchArgs) return undefined;
    const t = setTimeout(() => {
      fetchAdvice(...fetchArgs);
      setSettledSig(sig);
    }, EPA_ADVICE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (!cur || (!hasData && !visible)) { anchorSigRef.current = null; return null; }

  /* pin the card where the cursor was when this point became active; it no longer chases the mouse */
  const hoverSig = hasData ? sig || `${label}` : anchorSigRef.current;
  if (hasData && anchorSigRef.current !== hoverSig) {
    anchorSigRef.current = hoverSig;
    anchorRef.current = _epaMouse ? { ..._epaMouse } : anchorRef.current;
  }
  const mousePos = anchorRef.current;
  if (!mousePos) return null;

  let groundTruthField = null;
  let groundTruthStatus = null;
  if (typeof resolveStatus === "function") {
    curPayload.forEach(p => {
      const fld = p.dataKey || p.name || fieldKey || "";
      const status = resolveStatus(fld, p.value, p.payload);
      if (status && _epaAdviceSeverity(status) >= _epaAdviceSeverity(groundTruthStatus)) {
        groundTruthField = p;
        groundTruthStatus = status;
      }
    });
  }

  const analysing = hasData && (loading || (sig && settledSig !== sig));

  const adviceColor = groundTruthStatus
    ? (groundTruthStatus.startsWith("CRITICAL") ? C.red
       : groundTruthStatus.startsWith("WARNING") ? C.amber
       : groundTruthStatus.startsWith("EXCELLENT") ? C.green
       : groundTruthStatus.startsWith("GOOD") ? C.green
       : C.muted)
    : (!advice || analysing ? C.muted
       : advice.startsWith("■") ? C.red
       : advice.startsWith("▲") || advice.startsWith("▶") ? C.amber
       : advice.startsWith("▽") ? C.red
       : C.green);

  const TOOLTIP_W = 320;
  const TOOLTIP_H = 220;
  const OFFSET = 18;
  const ADVICE_MIN_H = 64;   // keeps the card the same size while "AI Analyzing..." swaps for the advice text
  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  const rawLeft = mousePos.x + OFFSET + TOOLTIP_W > viewW ? mousePos.x - TOOLTIP_W - OFFSET : mousePos.x + OFFSET;
  const rawTop  = mousePos.y + OFFSET + TOOLTIP_H > viewH ? mousePos.y - TOOLTIP_H - OFFSET : mousePos.y + OFFSET;
  const left = Math.min(Math.max(rawLeft, 8), viewW - TOOLTIP_W - 8);
  const top  = Math.min(Math.max(rawTop,  8), viewH - TOOLTIP_H - 8);

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${adviceColor}44`,
      borderLeft: `4px solid ${adviceColor}`, borderRadius: 12,
      padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 20px 50px rgba(0,0,0,0.7)",
      width: TOOLTIP_W, zIndex: 999999, pointerEvents: "none",
    }}>
<div style={{ color: C.cyan, fontWeight: 700, marginBottom: parseT(curPayload[0]?.payload?.time) != null ? 2 : 8, fontSize: 12 }}>{displayLabel}</div>
      {parseT(curPayload[0]?.payload?.time) != null && (
        <div style={{ color: C.amber, fontSize: 10, marginBottom: 8 }}>{fmtDT(parseT(curPayload[0].payload.time))}</div>
      )}
      {curPayload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 6, color: C.muted }}>
          <span style={{ fontSize: 10 }}>{p.name || p.dataKey}</span>
          <span style={{ color: p.color || C.cyan, fontWeight: 700, fontSize: 12 }}>
            {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
            {p.unit && <span style={{ color: C.muted, fontSize: 9, marginLeft: 4 }}>{p.unit}</span>}
          </span>
        </div>
      ))}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 10, minHeight: ADVICE_MIN_H }}>
        {analysing ? (
          <div style={{ color: C.muted, fontSize: 10 }}>AI Analyzing...</div>
        ) : advice ? (
          <div style={{ color: adviceColor, lineHeight: 1.6, fontSize: 10.5, wordWrap: "break-word" }}>
            {renderEPAAdvice(syncAdviceTone && groundTruthStatus ? syncToneToStatus(advice, groundTruthStatus) : advice)}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

/* Makes the advice icon (and the words critical / good) agree with the colour-coded status of the hovered bar */
function syncToneToStatus(advice, status) {
  let t = String(advice || "");
  const icon = status.startsWith("CRITICAL") ? "■" : status.startsWith("WARNING") ? "▲" : "◆";
  t = /^[◆■▲▽▶]\s/.test(t) ? icon + " " + t.slice(2) : `${icon} ${t}`;
  if (status.startsWith("WARNING")) {
    t = t.replace(/\bcritical(ly)?\b/gi, m => (m[0] === "C" ? "Warning" : "warning"));
  }
  return t;
}

function renderEPAAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round" /><line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" /><circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b" /></svg>,
    "■": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422" /><path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round" /></svg>,
    "◆": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22" /><path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    "▽": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    "▶": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return <span style={{ display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.5 }}>{icon}<span>{text.slice(sym.length + 1)}</span></span>;
    }
  }
  return <span>{text}</span>;
}

function CalcInput({ label, value, onChange, unit = "", hint = "", placeholder = "" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}{unit ? ` (${unit})` : ""}</label>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 6,
          color: C.cyan, fontFamily: MONO, fontSize: 12, padding: "6px 10px",
          outline: "none", width: "100%",
        }}
        onFocus={e => e.target.style.borderColor = C.cyan}
        onBlur={e => e.target.style.borderColor = C.border2}
      />
      {hint && <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{hint}</div>}
    </div>
  );
}

function EPAAISidebar({ context, trigger }) {
  const { advice, loading, fetchAdvice } = useAIAdvice();
  useEffect(() => {
    if (!trigger || !context) return;
    fetchAdvice("epa_compliance", context.score, "%", null, context, []);
  }, [trigger]);
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 10, padding: 14, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon name="bot" size={14} color={C.teal} />
        <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.teal, letterSpacing: 1 }}>EPA AI ADVISOR</span>
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, lineHeight: 1.7, minHeight: 50 }}>
        {loading
          ? <span style={{ color: C.muted }}>Analysing compliance data…</span>
          : advice
          ? <span style={{ color: C.text }}>{advice}</span>
          : <span>Load a tab to receive context-aware EPA guidance.</span>
        }
      </div>
    </div>
  );
}

function TabOverview({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/overview?well=${encodeURIComponent(well)}` : `${EPA_API}/overview`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, fontSize: 11, padding: 40 }}>Could not load overview. Ensure EPA service (port 5010) is running.</div>;

  const tierC  = tierColor(data.compliance_tier);
  const riskC  = riskColor(data.fleet_risk_level);
  const sparkData = (data.score_sparkline || []).map((v, i) => ({ i, score: v }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Compact score banner */}
      <div style={{
        background: `linear-gradient(135deg, ${tierC}08 0%, ${C.card} 70%)`,
        border: `1px solid ${tierC}33`, borderRadius: 12,
        padding: "16px 22px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap",
      }}>
        {/* Score + Tier inline */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
          <div style={{
            fontFamily: MONO, fontWeight: 700, fontSize: 36, color: tierC, lineHeight: 1,
            filter: `drop-shadow(0 0 10px ${tierC}44)`,
          }}>
            {typeof data.compliance_score === "number" ? data.compliance_score.toFixed(1) : "-"}
          </div>
          <div>
            <TierBadge tier={data.compliance_tier} short={data.compliance_tier_short} size="large" />
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 4 }}>/ 100 compliance score</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", marginBottom: 6 }}>
            <div style={{
              height: "100%", borderRadius: 3,
              width: `${data.compliance_score}%`,
              background: `linear-gradient(90deg, ${tierC}88, ${tierC})`,
              transition: "width 1.2s ease",
            }} />
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontFamily: MONO, fontSize: 10 }}><span style={{ color: C.muted }}>Risk: </span><span style={{ color: riskC, fontWeight: 700 }}>{data.fleet_risk_level}</span></span>
            <span style={{ fontFamily: MONO, fontSize: 10 }}><span style={{ color: C.muted }}>Cycles: </span><span style={{ color: C.cyan }}>{data.total_cycles?.toLocaleString()}</span></span>
            <span style={{ fontFamily: MONO, fontSize: 10 }}><span style={{ color: C.muted }}>Clean: </span><span style={{ color: C.green }}>{data.clean_cycles?.toLocaleString()}</span></span>
          </div>
        </div>

        {/* Sparkline */}
        <div style={{ width: 180, flexShrink: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>Score Trend</div>
          <ResponsiveContainer width="100%" height={55}>
            <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: -30, bottom: 0 }}>
              <defs>
                <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={tierC} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={tierC} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="score" stroke={tierC} strokeWidth={1.5} fill="url(#sparkGrad)" dot={false} />
              <YAxis domain={[0, 100]} hide />
              <XAxis dataKey="i" hide />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Tier legend */}
        <div style={{ width: "100%", borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginBottom: 6, letterSpacing: 1 }}>COMPLIANCE TIERS:</div>
          <TierLegend />
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
        <KpiCard label="Total CH₄ Emissions" value={fmtK(data.total_ch4_kg)} unit="kg" color={EPA.emit} icon="cloud" badge="Methane" sub={`CO₂e: ${fmtK(data.total_co2e_kg)} kg`} />
        <KpiCard label="Clean Cycle %" value={fmt(data.clean_cycle_pct * 100, 1)} unit="%" color={C.green} icon="check-circle" badge="Compliance" sub={`${data.clean_cycles?.toLocaleString()} of ${data.total_cycles?.toLocaleString()} cycles`} />
        <KpiCard label="Fleet Risk Level" value={data.fleet_risk_level} color={riskC} icon="shield" badge="Risk" sub={`Score: ${fmt(data.compliance_score, 1)} / 100`} />
        <KpiCard label="Total CO₂e Emissions" value={fmtK(data.total_co2e_kg)} unit="kg" color={EPA.emit} icon="globe" badge="GWP-100" sub="IPCC AR5 factor = 25×" />
        <KpiCard label="Vent Threshold (dynamic)" value={fmt(data.thresholds?.vent_threshold_min, 2)} unit="min" color={C.orange} icon="wind" badge="Dynamic" sub="25th pct of positive VentMin" />
      </div>

      {/* Monthly emissions */}
      <ChartCard title="Monthly CH₄ Emissions (kg)" sub="Estimated methane per month from venting events" accent={EPA.emit} height={220}>
        <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.monthly_emissions || []} margin={{ top: 6, right: 10, left: 4, bottom: 36 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} angle={-30} textAnchor="end"
              label={{ value: "Month", position: "insideBottom", offset: -28, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false}
              label={{ value: "CH₄ (kg)", angle: -90, position: "insideLeft", offset: 10, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice fieldKey="Monthly_CH4_Emissions" contextData={{ compliance_score: data.compliance_score, fleet_risk_level: data.fleet_risk_level }} />} />
            <Line type="monotone" dataKey="ch4_kg" name="CH₄ (kg)" stroke={EPA.emit} strokeWidth={2} dot={{ r: 3, fill: EPA.emit }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Thresholds */}
      <Card>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 12 }}>
          Active Thresholds: Sources: EPA
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
          {data.thresholds && Object.entries(data.thresholds).map(([k, v]) => {
            const meta = THRESHOLD_META[k] || { label: k.replace(/_/g, " "), unit: "", source: "" };
            return (
              <div key={k} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 12px" }}>
                <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>{meta.label}</div>
                <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: C.cyan }}>
                  {fmt(v, 3)}<span style={{ fontSize: 9, color: C.muted, marginLeft: 3 }}>{meta.unit}</span>
                </div>
                {meta.source && <div style={{ fontFamily: MONO, fontSize: 8, color: `${C.teal}88`, marginTop: 2 }}>{meta.source}</div>}
              </div>
            );
          })}
        </div>
      </Card>

      <EPAAISidebar context={{ score: data.compliance_score, tier: data.compliance_tier, risk: data.fleet_risk_level, ch4: data.total_ch4_kg, tab: "overview" }} trigger={data} />
    </div>
  );
}

// 2. Applicability Check Tab
function TabApplicability({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/applicability?well=${encodeURIComponent(well)}` : `${EPA_API}/applicability`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load applicability data.</div>;

  const { criteria = [], summary = {}, overall_applicability } = data;
  const overallColor = overall_applicability === "Applicable" ? C.green : overall_applicability === "Conditionally Applicable" ? C.amber : C.red;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ background: `${overallColor}0d`, border: `1.5px solid ${overallColor}44`, borderRadius: 12, padding: "16px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: overallColor }}>
          {overall_applicability}
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[{ label: "Pass", count: summary.pass, color: EPA.pass }, { label: "Marginal", count: summary.marginal, color: EPA.marginal }, { label: "Fail", count: summary.fail, color: EPA.fail }].map(({ label, count, color }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 24, color }}>{count}</div>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginLeft: "auto" }}>NSPS OOOO/OOOOa</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {criteria.map((c) => {
          const sc = statusColor(c.status);
          const iconName = c.status === "pass" ? "check" : c.status === "marginal" ? "alert-triangle" : "alert-circle";
          return (
            <Card key={c.id} style={{ padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: `${sc}18`, border: `2px solid ${sc}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={iconName} size={14} color={sc} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
                    <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text }}>{c.name}</span>
                    <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1, textTransform: "uppercase", color: sc, background: `${sc}18`, border: `1px solid ${sc}44`, borderRadius: 3, padding: "2px 7px" }}>{c.status}</span>
                    {c.equation && (
                      <span style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 3, padding: "2px 7px" }}>{c.equation}</span>
                    )}
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, marginLeft: "auto" }}>Value: {c.value}</span>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, lineHeight: 1.6, marginBottom: 3 }}>{c.explanation}</div>
                  {c.reference && (
                    <div style={{ fontFamily: MONO, fontSize: 9, color: `${C.teal}99`, borderLeft: `2px solid ${C.teal}33`, paddingLeft: 7, display: "flex", alignItems: "center", gap: 5 }}>
                      <Icon name="clipboard" size={10} color={C.teal} /> {c.reference}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// 3. Cycle Audit Tab
function TabCycleAudit({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback((off = 0) => {
    setLoading(true);
    const wellParam = well && well !== "all" ? `&well=${encodeURIComponent(well)}` : "";
    axios.get(withQs(`${EPA_API}/cycle-audit?limit=${limit}&offset=${off}${wellParam}`, winQs))
      .then(r => { setData(r.data); setOffset(off); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [well, limit, winQs]);

  useEffect(() => { load(0); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load cycle audit.</div>;

  const {
    cycles = [], total,
    page_ch4_kg, page_co2e_kg,          
    total_ch4_kg, total_co2e_kg,       
    page_clean, total_clean_cycles, total_clean_pct,
  } = data;
  const totalPages = Math.ceil(total / limit);
  const curPage    = Math.floor(offset / limit) + 1;

  function velColor(status) {
    return ({ optimal: C.green, pass: C.teal, subopt: C.blue, low: C.amber, high: C.red, fail: C.red })[status] || C.muted;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        <KpiCard label="Total Cycles" value={total?.toLocaleString() || "-"} color={C.cyan} icon="refresh" />
        <KpiCard
          label="Clean Cycles"
          value={total_clean_cycles ?? page_clean}
          color={C.green}
          icon="check-circle"
          sub={
            total != null && total_clean_pct != null
              ? `${(total_clean_pct * 100).toFixed(1)}% of ${total.toLocaleString()} total cycles`
              : `of ${cycles.length} on page`
          }
        />
      </div>

      {/* Flag legend */}
      <Card style={{ padding: "10px 14px" }}>
        <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Violation Flag Guide</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {Object.entries(FLAG_DESC).map(([f, desc]) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontFamily: MONO, fontSize: 8, color: flagColor(f), background: `${flagColor(f)}18`, border: `1px solid ${flagColor(f)}44`, borderRadius: 3, padding: "1px 5px" }}>{FLAG_FULL_NAMES[f] || f}</span>
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{desc}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
            <thead>
              <tr style={{ background: C.panel, borderBottom: `1px solid ${C.border}` }}>
              {["Cycle #", "Date/Time Open", "Velocity (ft/min)", "Load Factor", "ShutIn Eff ×", "Vent (min)", "CH₄ (kg)", "CO₂e (kg)", "Score / Tier", "Flags"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cycles.map((row, i) => {
                const sc = row.compliance_score ?? 0;
                const scoreC = tierColorFromScore(sc);
                return (
                  <tr key={i}
                    style={{ borderBottom: `1px solid ${C.border}`, background: row.is_clean_cycle ? `${C.green}05` : "transparent", transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = `${C.cyan}06`}
                    onMouseLeave={e => e.currentTarget.style.background = row.is_clean_cycle ? `${C.green}05` : "transparent"}
                  >
                    <td style={{ padding: "7px 10px", color: C.cyan }}>{row.cycle_serial}</td>
                    <td style={{ padding: "7px 10px", color: C.muted, fontSize: 9 }}>{row.date_open || "-"}</td>
                    <td style={{ padding: "7px 10px" }}><span style={{ color: velColor(row.velocity_status), fontWeight: 700 }}>{fmt(row.arrival_velocity_ftmin, 0)}</span></td>
                    <td style={{ padding: "7px 10px" }}><span style={{ color: row.load_factor_status === "pass" ? C.green : row.load_factor_status === "warn" ? C.amber : C.red }}>{fmt(row.load_factor, 4)}</span></td>
                    <td style={{ padding: "7px 10px", color: (row.shut_in_efficiency ?? 0) >= 1 ? C.teal : C.amber }}>{fmt(row.shut_in_efficiency, 3)}</td>
                    <td style={{ padding: "7px 10px", color: (row.vent_min ?? 0) > 0 ? C.orange : C.muted }}>{fmt(row.vent_min, 2)}</td>
                    <td style={{ padding: "7px 10px", color: EPA.emit }}>{fmt(row.estimated_ch4_kg, 4)}</td>
                    <td style={{ padding: "7px 10px", color: EPA.emit }}>{fmt(row.estimated_co2e_kg, 3)}</td>
                    <td style={{ padding: "7px 10px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 12, color: scoreC }}>{fmt(sc, 0)}</span>
                        <TierBadge tier={row.compliance_tier} short={row.compliance_tier_short} />
                      </div>
                    </td>
                    <td style={{ padding: "7px 10px" }}>
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        {(row.violation_flags || []).map(f => (
                          <span key={f} title={FLAG_DESC[f] || f} style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 0.3, color: flagColor(f), background: `${flagColor(f)}18`, border: `1px solid ${flagColor(f)}44`, borderRadius: 3, padding: "1px 5px", cursor: "help" }}>{FLAG_FULL_NAMES[f] || f}</span>
                        ))}
                        {(!row.violation_flags || row.violation_flags.length === 0) && <span style={{ color: C.green, fontSize: 9 }}>-</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderTop: `1px solid ${C.border}`, background: C.panel }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Page {curPage} of {totalPages} · {total} total cycles</span>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ label: "← Prev", off: offset - limit, disabled: offset <= 0 }, { label: "Next →", off: offset + limit, disabled: offset + limit >= total }].map(b => (
              <button key={b.label} disabled={b.disabled} onClick={() => load(Math.max(0, b.off))} style={{ background: b.disabled ? "transparent" : `${C.cyan}18`, border: `1px solid ${b.disabled ? C.border : C.cyan + "55"}`, borderRadius: 5, color: b.disabled ? C.muted : C.cyan, fontFamily: MONO, fontSize: 10, padding: "5px 12px", cursor: b.disabled ? "default" : "pointer" }}>{b.label}</button>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// 4. Venting Risk Tab
function TabVentingRisk({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/venting-risk?well=${encodeURIComponent(well)}` : `${EPA_API}/venting-risk`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load venting risk data.</div>;

  const { trend = [], high_risk_cycles = [], load_analysis = {}, monthly_venting = [] } = data;
  // unique position key (_k): cycle numbers repeat across wells, so the timestamp axis is keyed on position, not on cycle #
  const trendT = trend.map((d, i) => ({ ...d, _k: i, _t: parseT(d.time) }));
  const hasTv  = hasStamps(trendT);
  const lfFail = load_analysis.load_factor_fail_threshold ?? 0.5;
  const lfWarn = load_analysis.load_factor_warn_threshold ?? 0.40;

  const loadChartData = [
    { name: "Safe",      value: load_analysis.safe      || 0, color: C.green },
    { name: "Warn",      value: load_analysis.warn      || 0, color: C.amber },
    { name: "Overload",  value: load_analysis.overload  || 0, color: C.red   },
    { name: "Underload", value: load_analysis.underload || 0, color: C.muted },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
        <KpiCard label="Avg Risk Score" value={fmt(data.avg_risk_score, 1)} unit="/ 100" color={C.red} icon="alert-triangle" badge="Risk" sub="Higher = more risk" />
        <KpiCard label="Vent Threshold" value={fmt(data.vent_threshold_min, 2)} unit="min" color={C.orange} icon="wind" badge="Dynamic" sub="25th pct of +VentMin" />
        <KpiCard label="Overload Cycles" value={load_analysis.overload || 0} color={C.red} icon="alert-circle" sub={`Lf ≥ ${lfFail}`} />
        <KpiCard label="Warn Cycles" value={load_analysis.warn || 0} color={C.amber} icon="alert-triangle" sub={`${lfWarn} ≤ Lf < ${lfFail}`} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
      <ChartCard title="Venting Risk Score Trend" sub="Rolling 10-cycle risk average vs per-cycle risk" accent={C.red} height={265}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trendT} margin={{ top: 6, right: 12, left: 8, bottom: hasTv ? 66 : 46 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="cycle" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} tickFormatter={v => `C${v}`} height={hasTv ? 20 : undefined}
                label={hasTv ? undefined : { value: "Cycle #", position: "insideBottom", offset: -4, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
              {dateXAxis(trendT, "_k", 5, "Cycle #")}
              <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={44}
                label={{ value: "Risk Score", angle: -90, position: "insideLeft", offset: 10, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
              <Tooltip content={(
                <EPAHoverAdvice
                  labelFormatter={v => `Cycle ${v}`}
                  fieldKey="Venting_Risk_Score"
                  contextData={{ vent_threshold_min: data.vent_threshold_min, avg_risk_score: data.avg_risk_score }}
                  resolveStatus={(field, value) => {
                    if (value == null) return undefined;
                    if (field !== "risk_score" && field !== "rolling_risk") return undefined;
                    if (value >= 50) return "CRITICAL - venting risk score at or above the 50 threshold, high risk";
                    if (value >= 25) return "WARNING - venting risk score elevated, approaching threshold";
                    return "GOOD - venting risk score within acceptable range";
                  }}
                />
              )} />
              <Legend wrapperStyle={{ fontFamily: MONO, fontSize: 9, paddingTop: 14 }} />
              <Bar dataKey="risk_score" name="Risk Score" fill={C.cyan} radius={[2, 2, 0, 0]}>
                {trend.map((d, i) => {
                  const v = d.risk_score;
                  const clr = v == null ? C.muted : v >= 50 ? C.red : v >= 25 ? C.amber : C.green;
                  return <Cell key={`cell-${i}`} fill={`${clr}88`} />;
                })}
              </Bar>
              <Line
                type="monotone"
                dataKey="rolling_risk"
                name="Rolling Avg"
                stroke={`${C.muted}99`}
                strokeWidth={2}
                dot={(props) => {
                  const { cx, cy, value, index } = props;
                  const clr = value == null ? C.muted : value >= 50 ? C.red : value >= 25 ? C.amber : C.green;
                  return <circle key={`rr-dot-${index}`} cx={cx} cy={cy} r={2.5} fill={clr} stroke={clr} />;
                }}
                activeDot={(props) => {
                  const { cx, cy, value } = props;
                  const clr = value == null ? C.muted : value >= 50 ? C.red : value >= 25 ? C.amber : C.green;
                  return <circle cx={cx} cy={cy} r={5} fill={clr} stroke={C.card} strokeWidth={2} />;
                }}
              />
              <ReferenceLine y={50} stroke={C.amber} strokeDasharray="4 4" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Load Factor Distribution" sub={`Lf = (Pc−Pt)/(Pc−Pl) · Fail ≥ ${lfFail}`} accent={C.amber} height={240}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, padding: "6px 0" }}>
            {loadChartData.map(d => {
              const total = loadChartData.reduce((s, x) => s + x.value, 0) || 1;
              const pct = (d.value / total) * 100;
              return (
                <div key={d.name}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: d.color }}>{d.name}</span>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: C.text, fontWeight: 700 }}>{d.value.toLocaleString()} <span style={{ color: C.muted }}>({pct.toFixed(1)}%)</span></span>
                  </div>
                  <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: d.color, borderRadius: 3, transition: "width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Monthly Venting Events & CH₄ Emissions" sub="Vent events = cycles exceeding dynamic threshold" accent={EPA.emit} height={270}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={monthly_venting} margin={{ top: 6, right: 40, left: 8, bottom: 70 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} angle={-30} textAnchor="end"
              label={{ value: "Month", position: "insideBottom", offset: -45, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <YAxis yAxisId="left" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={44}
              label={{ value: "Vent Events", angle: -90, position: "insideLeft", offset: 10, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: EPA.emit, fontSize: 8, fontFamily: MONO }} tickLine={false} width={44}
              label={{ value: "CH₄ (kg)", angle: 90, position: "insideRight", offset: 10, fill: EPA.emit, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice fieldKey="Monthly_Venting_Events" contextData={{ vent_threshold_min: data.vent_threshold_min }} />} />
            <Legend verticalAlign="top" height={30} wrapperStyle={{ fontFamily: MONO, fontSize: 10 }}
              formatter={(v, e) => <span style={{ color: e.color, fontWeight: 700 }}>{v === "Vent Events" ? "Bars: Vent Events (left axis)" : "Line: CH₄ (kg) (right axis)"}</span>} />
            <Bar yAxisId="left" dataKey="vent_events" name="Vent Events" fill={C.orange} radius={[3, 3, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="total_ch4_kg" name="CH₄ (kg)" stroke={EPA.emit} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <Card>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.red, marginBottom: 12 }}>Top 10 Highest-Risk Cycles</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Cycle #", "Date", "Risk Score", "Velocity (ft/min)", "Load Factor", "Vent (min)", "CH₄ (kg)", "Flags"].map(h => (
                  <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {high_risk_cycles.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 10px", color: C.cyan }}>{r.cycle_serial}</td>
                  <td style={{ padding: "7px 10px", color: C.muted, fontSize: 9 }}>{r.date || "-"}</td>
                  <td style={{ padding: "7px 10px" }}><span style={{ color: C.red, fontWeight: 700 }}>{fmt(r.risk_score, 1)}</span></td>
                  <td style={{ padding: "7px 10px", color: C.amber }}>{fmt(r.arrival_velocity_ftmin, 0)}</td>
                  <td style={{ padding: "7px 10px", color: C.orange }}>{fmt(r.load_factor, 4)}</td>
                  <td style={{ padding: "7px 10px", color: C.orange }}>{fmt(r.vent_min, 2)}</td>
                  <td style={{ padding: "7px 10px", color: EPA.emit }}>{fmt(r.estimated_ch4_kg, 4)}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      {(r.violation_flags || []).map(f => (
                        <span key={f} title={FLAG_DESC[f] || f} style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 0.3, color: flagColor(f), background: `${flagColor(f)}18`, border: `1px solid ${flagColor(f)}44`, borderRadius: 3, padding: "1px 5px", cursor: "help" }}>{FLAG_FULL_NAMES[f] || f}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// 5. Compliance Scorecard Tab
function TabScorecard({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/scorecard?well=${encodeURIComponent(well)}` : `${EPA_API}/scorecard`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load scorecard data.</div>;

  const { components = [], overall_score, compliance_tier, compliance_tier_short, overall_prev, overall_delta } = data;
  const tierC  = tierColor(compliance_tier);
  const deltaC = overall_delta >= 0 ? C.green : C.red;

  const radarData = components.map(c => ({
    metric:   c.label,               
    current:  c.current,
    previous: c.previous,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <Card accentTop={tierC} style={{ flex: "0 0 auto", textAlign: "center", padding: "20px 28px" }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Overall</div>
          <TierBadge tier={compliance_tier} short={compliance_tier_short} size="large" />
          <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 30, color: tierC, marginTop: 8 }}>{fmt(overall_score, 1)}</div>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 2 }}>/ 100</div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: deltaC, marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <Icon name={overall_delta >= 0 ? "trending-up" : "trending-down"} size={12} color={deltaC} />
            {overall_delta >= 0 ? "+" : ""}{fmt(overall_delta, 1)} vs prior
          </div>
          <div style={{ marginTop: 10 }}>
            <TierLegend compact />
          </div>
        </Card>

        <Card style={{ flex: 1, minWidth: 340 }}>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 2 }}>Component Comparison: Current vs Previous Period</div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginBottom: 8 }}>
            Longer, greener bars = stronger compliance on that component. Dashed lines mark the Poor / Fair / Good cutoffs.
          </div>
          <ResponsiveContainer width="100%" height={Math.max(260, radarData.length * 42 + 40)}>
            <BarChart data={radarData} layout="vertical" margin={{ top: 8, right: 28, left: 4, bottom: 34 }} barGap={4}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                label={{ value: "Compliance Score (%)", position: "insideBottom", offset: -20, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
              <YAxis type="category" dataKey="metric" width={190} tick={{ fill: C.text, fontSize: 10, fontFamily: MONO }}
                label={{ value: "Component", angle: -90, position: "insideLeft", offset: 0, dx: -60, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
              <ReferenceLine x={60} stroke={C.red} strokeDasharray="3 3" />
              <ReferenceLine x={80} stroke={C.green} strokeDasharray="3 3" />
              <Tooltip content={(
                <EPAHoverAdvice
                  fieldKey="Compliance_Component_Score"
                  contextData={{ overall_score, compliance_tier }}
                  resolveStatus={(field, value) => {
                    if (field !== "current" || value == null) return undefined;
                    if (value >= 80) return "GOOD - component score in the strong/compliant range";
                    if (value >= 60) return "WARNING - component score in the fair/marginal range";
                    return "CRITICAL - component score in the poor/non-compliant range";
                  }}
                />
              )} />
              <Bar dataKey="previous" name="Previous" fill={C.muted} fillOpacity={0.35} stroke={C.muted} strokeWidth={1} radius={[0, 3, 3, 0]} barSize={7}>
                <LabelList dataKey="previous" position="right" formatter={v => v == null ? "" : `${fmt(v, 0)}%`} style={{ fontFamily: MONO, fontSize: 9, fill: C.text, fontWeight: 700 }} />
              </Bar>
              <Bar dataKey="current" name="Current" radius={[0, 3, 3, 0]} barSize={16}>
                {radarData.map((d, i) => (
                  <Cell key={i} fill={d.current >= 80 ? C.green : d.current >= 60 ? C.amber : C.red} />
                ))}
                <LabelList dataKey="current" position="right" formatter={v => `${fmt(v, 0)}%`} style={{ fontFamily: MONO, fontSize: 9, fill: C.text, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <PlotSummary inline accent={C.cyan}
            title="Compliance Component Scores: Current vs Previous Period"
            subtitle="EPA compliance score (0 to 100) of each scorecard component in the current period against the previous period; below 60 is poor, 80 and above is good">
            <BarChart data={radarData}>
              <XAxis dataKey="metric" />
              <YAxis label={{ value: "Compliance score (%)" }} />
              <ReferenceLine y={60} label={{ value: "60 poor / fair cutoff" }} />
              <ReferenceLine y={80} label={{ value: "80 fair / good cutoff" }} />
              <Bar dataKey="previous" name="Previous" />
              <Bar dataKey="current" name="Current" />
            </BarChart>
          </PlotSummary>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 16, height: 8, borderRadius: 2, background: C.muted, opacity: 0.35, border: `1px solid ${C.muted}`, display: "inline-block" }} />
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Previous (thin uncolored bar, no tier color)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 16, height: 8, borderRadius: 2, background: C.green, display: "inline-block" }} />
              <span style={{ width: 16, height: 8, borderRadius: 2, background: C.amber, display: "inline-block" }} />
              <span style={{ width: 16, height: 8, borderRadius: 2, background: C.red, display: "inline-block" }} />
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Current (thick bar, color = tier, see below)</span>
            </div>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 6 }}>
            <span style={{ color: C.green }}>■</span>&nbsp;80, 100 Good&nbsp;&nbsp;
            <span style={{ color: C.amber }}>■</span>&nbsp;60, 79 Fair&nbsp;&nbsp;
            <span style={{ color: C.red }}>■</span>&nbsp;&lt;60 Poor
          </div>
        </Card>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {components.map((c) => {
          const dc   = c.delta >= 0 ? C.green : C.red;
          const barC = c.current >= 80 ? C.green : c.current >= 60 ? C.amber : C.red;
          return (
            <Card key={c.key} style={{ padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text }}>{c.label}</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 1 }}>{c.description}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Prev</div>
                    <div style={{ fontFamily: MONO, fontSize: 14, color: C.muted }}>{fmt(c.previous, 1)}%</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Current</div>
                    <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: barC }}>{fmt(c.current, 1)}%</div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 50 }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Δ</div>
                    <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: dc }}>{c.delta >= 0 ? "+" : ""}{fmt(c.delta, 1)}</div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 8, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${c.current}%`, height: "100%", background: barC, borderRadius: 3, transition: "width 0.8s ease" }} />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// 6. Asset Ranking Tab
function TabAssetRanking({ winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(withQs(`${EPA_API}/asset-ranking`, winQs)).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load asset ranking.</div>;

  const { rankings = [], fleet_avg_score, best_score } = data;

  if (rankings.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <SectionTitle accent={C.amber}>Asset Ranking</SectionTitle>
        <Card>
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, padding: 16, textAlign: "center" }}>
            Only one well in dataset. Asset ranking requires multiple wells.<br />
            <span style={{ color: C.cyan }}>Configure multiple wells to enable this tab.</span>
          </div>
        </Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
          <KpiCard label="Fleet Avg Score" value={fmt(fleet_avg_score, 1)} unit="/ 100" color={tierColorFromScore(fleet_avg_score ?? 0)} icon="bar-chart" />
          <KpiCard label="Best Score" value={fmt(best_score, 1)} unit="/ 100" color={C.green} icon="award" />
        </div>
      </div>
    );
  }

  const chartData = rankings.map(r => ({ name: r.short_name, score: r.compliance_score, ch4: r.total_ch4_kg }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
        <KpiCard label="Fleet Avg Score" value={fmt(fleet_avg_score, 1)} unit="/ 100" color={tierColorFromScore(fleet_avg_score ?? 0)} icon="bar-chart" />
        <KpiCard label="Best Score" value={fmt(best_score, 1)} unit="/ 100" color={C.green} icon="award" />
        <KpiCard label="Wells Ranked" value={rankings.length} color={C.cyan} icon="tool" />
      </div>

      <ChartCard title="EPA Compliance Score by Well" sub="Ranked descending · fleet average overlaid · hover for tier" accent={C.cyan} height={290}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 6, right: 12, left: 8, bottom: 42 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false}
              label={{ value: "Well", position: "insideBottom", offset: -8, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={44}
              label={{ value: "Compliance Score", angle: -90, position: "insideLeft", offset: 10, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice fieldKey="Well_Compliance_Score" contextData={{ fleet_avg_score, best_score }} />} />
            <ReferenceLine y={fleet_avg_score} stroke={C.amber} strokeDasharray="5 5" label={{ value: "Fleet Avg", fill: C.amber, fontSize: 9, fontFamily: MONO }} />
            <Bar dataKey="score" name="Compliance Score" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, i) => <Cell key={i} fill={WELL_COLORS[i % WELL_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <Card>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 12 }}>Full Asset Rankings</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Rank", "Well", "Score", "Tier", "Risk", "Cycles", "Clean %", "CH₄ (kg)", "Avg Velocity", "Avg Load Factor"].map(h => (
                  <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rankings.map((r) => {
                const tc = tierColor(r.compliance_tier);
                const rc = riskColor(r.risk_level);
                return (
                  <tr key={r.rank} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "9px 10px" }}><span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 13, color: r.rank <= 3 ? C.amber : C.muted }}>#{r.rank}</span></td>
                    <td style={{ padding: "9px 10px", color: C.cyan, fontWeight: 700 }}>{r.short_name}</td>
                    <td style={{ padding: "9px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 52, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}><div style={{ width: `${r.compliance_score}%`, height: "100%", background: tc }} /></div>
                        <span style={{ color: tc, fontWeight: 700 }}>{fmt(r.compliance_score, 1)}</span>
                      </div>
                    </td>
                    <td style={{ padding: "9px 10px" }}><TierBadge tier={r.compliance_tier} short={r.tier_short} /></td>
                    <td style={{ padding: "9px 10px" }}><span style={{ color: rc, background: `${rc}18`, border: `1px solid ${rc}44`, borderRadius: 3, padding: "2px 6px", fontSize: 8, letterSpacing: 1, textTransform: "uppercase" }}>{r.risk_level}</span></td>
                    <td style={{ padding: "9px 10px", color: C.text }}>{(r.total_cycles || 0).toLocaleString()}</td>
                    <td style={{ padding: "9px 10px", color: C.green }}>{r.clean_pct != null ? fmt(r.clean_pct * 100, 1) + "%" : "-"}</td>
                    <td style={{ padding: "9px 10px", color: EPA.emit }}>{fmt(r.total_ch4_kg, 2)}</td>
                    <td style={{ padding: "9px 10px", color: C.amber }}>{fmt(r.avg_velocity, 0)} ft/min</td>
                    <td style={{ padding: "9px 10px", color: C.orange }}>{fmt(r.avg_load_factor, 4)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// 7. Emissions Inventory Tab
function TabEmissions({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("monthly");

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/emissions-inventory?well=${encodeURIComponent(well)}` : `${EPA_API}/emissions-inventory`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load emissions inventory.</div>;

  const rows      = view === "monthly" ? data.monthly || [] : data.quarterly || [];
  const totalCH4  = rows.reduce((s, r) => s + (r.ch4_kg || 0), 0);
  const totalCO2e = rows.reduce((s, r) => s + (r.co2e_kg || 0), 0);
  const totalVent = rows.reduce((s, r) => s + (r.vent_events || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <SectionTitle accent={EPA.emit}>Emissions Inventory</SectionTitle>
        <div style={{ display: "flex", gap: 5, marginLeft: "auto" }}>
          {["monthly", "quarterly"].map(v => (
            <button key={v} onClick={() => setView(v)} style={{ background: view === v ? `${EPA.emit}22` : "transparent", border: `1px solid ${view === v ? EPA.emit + "66" : C.border}`, borderRadius: 5, color: view === v ? EPA.emit : C.muted, fontFamily: MONO, fontSize: 9, padding: "4px 12px", cursor: "pointer", textTransform: "uppercase", letterSpacing: 1 }}>{v}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
        <KpiCard label="Total CH₄" value={fmtK(totalCH4)} unit="kg" color={EPA.emit} icon="cloud" sub={`CH₄ density: ${data.ch4_density_kg_per_mcf} kg/MCF`} />
        <KpiCard label="Total CO₂e" value={fmtK(totalCO2e)} unit="kg" color={EPA.emit} icon="globe" sub={`GWP-100 factor: ${data.co2e_factor}×`} />
        <KpiCard label="Total Vent Events" value={totalVent} color={C.orange} icon="wind" sub={data.emission_formula} />
      </div>
      <ChartCard title={`CH₄ & CO₂ Emissions, ${view === "monthly" ? "Monthly" : "Quarterly"}`} sub="VentMin × vent factor × CH₄ density. GWP-100 = 25 [IPCC AR5]" accent={EPA.emit} height={280}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 6, right: 12, left: 14, bottom: 66 }}>
            <defs>
              <linearGradient id="ch4Grad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={EPA.emit} stopOpacity={0.4} /><stop offset="95%" stopColor={EPA.emit} stopOpacity={0} /></linearGradient>
              <linearGradient id="co2Grad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.orange} stopOpacity={0.3} /><stop offset="95%" stopColor={C.orange} stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="period" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} angle={-30} textAnchor="end"
              label={{ value: "Period", position: "insideBottom", offset: -45, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={58}
              label={{ value: "Emissions (kg)", angle: -90, position: "insideLeft", offset: -6, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice fieldKey="Emissions_Inventory" contextData={{ ch4_density: data.ch4_density_kg_per_mcf, co2e_factor: data.co2e_factor }} />} />
            <Legend wrapperStyle={{ fontFamily: MONO, fontSize: 9, paddingTop: 20 }} />
            <Area type="monotone" dataKey="ch4_kg" name="CH₄ (kg)" stroke={EPA.emit} fill="url(#ch4Grad)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="co2e_kg" name="CO₂e (kg)" stroke={C.orange} fill="url(#co2Grad)" strokeWidth={1.5} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Period", "CH₄ (kg)", "CO₂e (kg)", "Cycles", "Vent Events", "Avg Vent (min)"].map(h => (
                <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 10px", color: C.cyan }}>{r.period}</td>
                  <td style={{ padding: "7px 10px", color: EPA.emit }}>{fmt(r.ch4_kg, 4)}</td>
                  <td style={{ padding: "7px 10px", color: EPA.emit }}>{fmt(r.co2e_kg, 3)}</td>
                  <td style={{ padding: "7px 10px", color: C.text }}>{r.cycles}</td>
                  <td style={{ padding: "7px 10px", color: r.vent_events > 0 ? C.orange : C.muted }}>{r.vent_events}</td>
                  <td style={{ padding: "7px 10px", color: C.muted }}>{fmt(r.avg_vent_min, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// 8. Compliance Timeline Tab
function TabTimeline({ well, winQs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = withQs(well && well !== "all" ? `${EPA_API}/timeline?well=${encodeURIComponent(well)}` : `${EPA_API}/timeline`, winQs);
    axios.get(url).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [well, winQs]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>Could not load timeline.</div>;

  const timeline = data.timeline || [];
  const events   = timeline.filter(t => t.event);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ChartCard
        title="Monthly Compliance Score History"
        sub="FC ≥ 85 · C ≥ 70 · M ≥ 55 · AR ≥ 40 · NC < 40"
        accent={C.cyan}
        height={290}
        footer={<TierLegend />}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={timeline} margin={{ top: 6, right: 12, left: 14, bottom: 46 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} angle={-30} textAnchor="end"
              label={{ value: "Month", position: "insideBottom", offset: -8, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={58}
              label={{ value: "Compliance Score", angle: -90, position: "insideLeft", offset: -6, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <YAxis yAxisId="cycles" orientation="right" domain={[0, "auto"]} allowDecimals={false} tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={50}
              label={{ value: "Cycles", angle: 90, position: "insideRight", offset: 6, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
            <Legend verticalAlign="top" height={30} wrapperStyle={{ fontFamily: MONO, fontSize: 10 }}
              formatter={(v, e) => <span style={{ color: v === "Cycles" ? C.text : e.color, fontWeight: 700 }}>{v === "Cycles" ? "Bars: Cycles per month (right axis)" : "Line: Avg Compliance Score (left axis)"}</span>} />
            <Tooltip content={<EPAHoverAdvice fieldKey="Compliance_Score_Timeline" contextData={{}} />} />
            {TIER_LINES.map(({ y, color, label }) => (
              <ReferenceLine key={y} y={y} stroke={color} strokeDasharray="3 3" label={{ value: label, fill: color, fontSize: 8, fontFamily: MONO }} />
            ))}
            <Bar dataKey="cycles" name="Cycles" fill={`${C.muted}66`} radius={[2, 2, 0, 0]} yAxisId="cycles" />
            <Area type="monotone" dataKey="avg_score" name="Avg Score" stroke={C.cyan} fill={`${C.cyan}18`} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {events.length > 0 && (
        <Card>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 12 }}>Compliance Tier Change Events</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {events.map((t, i) => {
              const isImproved = t.event?.includes("improved");
              const ec = isImproved ? C.green : C.red;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, background: `${ec}08`, border: `1px solid ${ec}22`, borderRadius: 7, padding: "8px 12px" }}>
                  <Icon name={isImproved ? "trending-up" : "trending-down"} size={16} color={ec} />
                  <div>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan }}>{t.month} </span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: ec }}>{t.event}</span>
                  </div>
                  <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 9, color: C.muted }}>Score: {fmt(t.avg_score, 1)} · {t.cycles} cycles</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 12 }}>Full Compliance History</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Month", "Avg Score", "Tier", "Cycles", "Event"].map(h => (
                <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 8, letterSpacing: 1, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {timeline.map((t, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 10px", color: C.cyan }}>{t.month}</td>
                  <td style={{ padding: "7px 10px", color: tierColor(t.tier), fontWeight: 700 }}>{fmt(t.avg_score, 1)}</td>
                  <td style={{ padding: "7px 10px" }}><TierBadge tier={t.tier} short={t.tier_short} /></td>
                  <td style={{ padding: "7px 10px", color: C.muted }}>{t.cycles}</td>
                  <td style={{ padding: "7px 10px", color: t.event ? (t.event.includes("improved") ? C.green : C.red) : C.muted, fontSize: 9 }}>{t.event || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function TabCyclePlots({ well, winQs }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const wellParam = well && well !== "all" ? `?well=${encodeURIComponent(well)}` : "";
    axios.get(withQs(`${EPA_API}/cycle-plots${wellParam}`, winQs))
      .then(r => setData(r.data))
      .catch(e => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [well, winQs]);  

  if (loading) return <Spinner />;
  if (error)   return <div style={{ color: C.red, fontFamily: MONO, padding: 40 }}>Error: {error}</div>;
  if (!data || !data.cycles?.length)
    return <div style={{ color: C.muted, fontFamily: MONO, padding: 40 }}>No cycle data available.</div>;

  const cycles    = data.cycles.map(d => ({ ...d, _t: parseT(d.time) }));
  const hasT      = hasStamps(cycles);
  const thresholds = data.thresholds || {};
  const VEL_EPA_MIN = thresholds.vel_epa_min ?? 350;
  const VEL_EPA_MAX = thresholds.vel_epa_max ?? 1200;
  const VEL_OPT_MIN = thresholds.vel_opt_min ?? 500;
  const VEL_OPT_MAX = thresholds.vel_opt_max ?? 1000;
  const VENT_THRESH = data.vent_threshold ?? 1.0;
  const PR_MIN      = thresholds.pr_min ?? 1.5;
  const wellLabel = well && well !== "all" ? well.split("/").pop() : "All Wells";
  const wellNamesData  = data.well_names || [];
  const isMultiWell    = !well || well === "all";
  const wellColorMap   = {};
  wellNamesData.forEach((w, i) => { wellColorMap[w] = WELL_COLORS[i % WELL_COLORS.length]; });
  function getWellColor(record, fallback) {
    if (isMultiWell && record.well_name && wellColorMap[record.well_name]) {
      return wellColorMap[record.well_name];
    }
    return fallback;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Header banner */}
      <Card style={{ padding: "10px 16px", background: `${C.cyan}06`, borderColor: `${C.cyan}33` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, lineHeight: 1.7 }}>
            <strong style={{ color: C.green }}>Cycle Plots, {wellLabel}</strong>
            {" "}<span style={{ color: C.muted }}>· {cycles.length} cycles · NSPS OOOO/OOOOa / WP physics</span>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[
              { label: "Total CH₄", value: `${(data.total_ch4_kg ?? 0).toFixed(3)} kg`, color: C.purple },
              { label: "Venting Cycles", value: `${data.vent_cycles ?? 0}`, color: C.orange },
              { label: "Clean Cycles", value: `${data.clean_cycles ?? 0}`, color: C.green },
              { label: "Avg Score", value: `${data.avg_score ?? 0}`, color: C.cyan },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{label}</div>
                <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>
      {isMultiWell && wellNamesData.length > 1 && (
        <Card style={{ padding: "8px 14px" }}>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Well Colors</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {wellNamesData.map((w, i) => (
              <div key={w} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: WELL_COLORS[i % WELL_COLORS.length] }} />
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.text }}>{w.split("/").pop()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <ChartCard
        title="CH₄ Emissions per Cycle"
        accent={C.purple}
        height={240}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cycles} margin={{ top: 6, right: 12, left: 0, bottom: hasT ? 22 : 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="idx" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined}
              label={hasT ? undefined : { value: "Cycle #", position: "insideBottomRight", offset: -4, fill: C.muted, fontSize: 8 }} />
            {dateXAxis(cycles, "idx", 5, "Cycle #")}
            <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} unit=" kg" width={52}
              label={{ value: "CH₄ (kg)", angle: -90, position: "insideLeft", offset: 6, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice labelFormatter={v => `Cycle ${v}`} fieldKey="Cycle_CH4_Emissions" contextData={{ vent_threshold: VENT_THRESH }} />} />
            <Bar dataKey="ch4_kg" name="CH₄ (kg)" radius={[2, 2, 0, 0]}>
              {cycles.map((d, i) => (
                <Cell key={i} fill={getWellColor(d, d.ch4_kg > 0 ? C.purple : `${C.muted}33`)} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="co2e_kg" name="CO₂e (kg)" stroke={C.pink}
              strokeWidth={1} dot={false} strokeDasharray="4 2" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard lensId="velocity"
        title="Arrival Velocity per Cycle: EPA NSPS OOOO/OOOOa Compliance Bands"
        accent={C.cyan}
        height={260}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cycles} margin={{ top: 6, right: 88, left: 14, bottom: hasT ? 22 : 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="idx" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined}
              label={hasT ? undefined : { value: "Cycle #", position: "insideBottomRight", offset: -4, fill: C.muted, fontSize: 8 }} />
            {dateXAxis(cycles, "idx", 5, "Cycle #")}
            <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} unit=" ft/min" width={78}
              label={{ value: "Velocity (ft/min)", angle: -90, position: "insideLeft", offset: -10, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={(
              <EPAHoverAdvice
                labelFormatter={v => `Cycle ${v}`}
                fieldKey="Cycle_Arrival_Velocity"
                contextData={{ vel_epa_min: VEL_EPA_MIN, vel_epa_max: VEL_EPA_MAX, vel_opt_min: VEL_OPT_MIN, vel_opt_max: VEL_OPT_MAX }}
                resolveStatus={(field, value) => {
                  if (field !== "velocity" || value == null) return undefined;
                  if (value >= VEL_OPT_MIN && value <= VEL_OPT_MAX) return "EXCELLENT - within WP §4.6 optimal velocity band";
                  if (value >= VEL_EPA_MIN && value <= VEL_EPA_MAX) return "GOOD - within EPA NSPS OOOO/OOOOa compliance band";
                  if (value < VEL_EPA_MIN) return "WARNING - velocity too low, below EPA NSPS OOOO/OOOOa minimum threshold";
                  return "CRITICAL - velocity exceeds EPA NSPS OOOO/OOOOa maximum limit, non-compliant";
                }}
              />
            )} />
            <ReferenceLine y={VEL_EPA_MIN} stroke={C.teal}  strokeDasharray="5 3"
              label={{ value: `Min ${fmt(VEL_EPA_MIN, 0)}`, fill: C.teal,  fontSize: 8, position: "right", dy: 10 }} />
            <ReferenceLine y={VEL_EPA_MAX} stroke={C.teal}  strokeDasharray="5 3"
              label={{ value: `Max ${fmt(VEL_EPA_MAX, 0)}`, fill: C.teal,  fontSize: 8, position: "right", dy: -10 }} />
            <ReferenceLine y={VEL_OPT_MIN} stroke={C.green} strokeDasharray="2 4"
              label={{ value: `Opt Min ${fmt(VEL_OPT_MIN, 0)}`,    fill: C.green, fontSize: 8, position: "right", dy: -10 }} />
            <ReferenceLine y={VEL_OPT_MAX} stroke={C.green} strokeDasharray="2 4"
              label={{ value: `Opt Max ${fmt(VEL_OPT_MAX, 0)}`,    fill: C.green, fontSize: 8, position: "right", dy: 10 }} />
            <Bar dataKey="velocity" name="Velocity (ft/min)" radius={[1, 1, 0, 0]}>
              {cycles.map((d, i) => {
                const v = d.velocity;
                const clr =
                  v == null       ? C.muted :
                  v >= VEL_OPT_MIN && v <= VEL_OPT_MAX ? C.green :
                  v >= VEL_EPA_MIN && v <= VEL_EPA_MAX  ? C.teal :
                  v < VEL_EPA_MIN ? C.amber : C.red;
                return <Cell key={i} fill={clr} />;
              })}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard lensId="volume"
        title="Gas Volume per Cycle (MCF): Production Efficiency"
        accent={C.blue}
        height={240}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cycles} margin={{ top: 6, right: 68, left: 14, bottom: hasT ? 22 : 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="idx" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined}
              label={hasT ? undefined : { value: "Cycle #", position: "insideBottomRight", offset: -4, fill: C.muted, fontSize: 8 }} />
            {dateXAxis(cycles, "idx", 5, "Cycle #")}
            <YAxis tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} unit=" ft/min" width={72}
              label={{ value: "Volume (MCF)", angle: -90, position: "insideLeft", offset: -6, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice labelFormatter={v => `Cycle ${v}`} fieldKey="Cycle_Gas_Volume" contextData={{}} />} />
            <Bar dataKey="vol_cycle_mcf" name="Vol (MCF)" radius={[2, 2, 0, 0]}>
              {cycles.map((d, i) => (
                <Cell key={i} fill={getWellColor(d, `${C.blue}${d.is_venting ? "66" : "cc"}`)} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="vol_cycle_mcf" name="Trend" stroke={C.cyan}
              strokeWidth={1} dot={false} strokeDasharray="4 2" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard lensId="pc-pl"
        title="Casing Pressure Buildup per Cycle; EPA"
        accent={C.amber}
        height={260}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cycles} margin={{ top: 6, right: 12, left: 0, bottom: hasT ? 22 : 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="idx" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined}
              label={hasT ? undefined : { value: "Cycle #", position: "insideBottomRight", offset: -4, fill: C.muted, fontSize: 8 }} />
            {dateXAxis(cycles, "idx", 5, "Cycle #")}
            <YAxis yAxisId="psi" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} unit=" psi" width={52}
              label={{ value: "Pressure (psi)", angle: -90, position: "insideLeft", offset: 6, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <YAxis yAxisId="ratio" orientation="right" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false}
              unit="×" width={36} domain={[0, 'auto']}
              label={{ value: "Pc/Pl Ratio (×)", angle: 90, position: "insideRight", offset: 6, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice labelFormatter={v => `Cycle ${v}`} fieldKey="Cycle_Pressure_Buildup" contextData={{ pr_min: PR_MIN }} />} />
            <ReferenceLine yAxisId="ratio" y={PR_MIN} stroke={C.amber} strokeDasharray="5 3"
              label={{ value: `Min ratio ${PR_MIN}×`, fill: C.amber, fontSize: 8 }} />
            <Bar yAxisId="psi" dataKey="csg_at_open"  name="CsgAtOpen (psi)"  radius={[2, 2, 0, 0]} fill={`${C.cyan}88`}  />
            <Bar yAxisId="psi" dataKey="csg_at_close" name="CsgAtClose (psi)" radius={[2, 2, 0, 0]} fill={`${C.teal}66`}  />
            <Line yAxisId="ratio" type="monotone" dataKey="pressure_ratio" name="Pc/Pl Ratio (×)"
              stroke={C.amber} strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 14, padding: "4px 0 0 8px" }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.cyan  }}>■ CsgAtOpen</span>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.teal  }}>■ CsgAtClose</span>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.amber }}>: Pc/Pl ratio (right axis)</span>
        </div>
      </ChartCard>
      <ChartCard
        title="Compliance Score per Cycle: Composite (0, 100)"
        accent={C.green}
        height={250}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cycles} margin={{ top: 6, right: 12, left: 0, bottom: hasT ? 22 : 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="idx" tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined}
              label={hasT ? undefined : { value: "Cycle #", position: "insideBottomRight", offset: -4, fill: C.muted, fontSize: 8 }} />
            {dateXAxis(cycles, "idx", 5, "Cycle #")}
            <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 8, fontFamily: MONO }} tickLine={false} width={36}
              label={{ value: "Score (0, 100)", angle: -90, position: "insideLeft", offset: 6, fill: C.muted, fontSize: 8, fontFamily: MONO }} />
            <Tooltip content={<EPAHoverAdvice labelFormatter={v => `Cycle ${v}`} fieldKey="Cycle_Compliance_Score" contextData={{}} resolveStatus={(fld, v) => (fld === "score" ? tierStatusFromScore(v) : undefined)} syncAdviceTone />} />
            {TIER_LINES.map(({ y, color, label: lbl }) => (
              <ReferenceLine key={y} y={y} stroke={color} strokeDasharray="3 3"
                label={{ value: lbl, fill: color, fontSize: 8, fontFamily: MONO }} />
            ))}
            <Bar dataKey="score" name="Cycle Score" radius={[1, 1, 0, 0]}>
              {cycles.map((d, i) => (
                <Cell key={i} fill={tierColorFromScore(d.score)} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}


// 10. EPA Calculator Tab
function TabCalculator() {
  const [inputs, setInputs] = useState({
    well_depth_ft: "",
    casing_psi: "",
    tubing_psi: "",
    line_psi: "",
    liquid_bbl: "",
    gas_rate_mcfd: "",
    vent_min: "",
    vol_mcf: "",
    num_wells: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function set(k) { return v => setInputs(p => ({ ...p, [k]: v })); }

  async function calculate() {
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${EPA_API}/calculator`, inputs);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  }

  function StatusBadge({ status }) {
    const color = status === "pass" ? C.green : status === "marginal" ? C.amber : status === "fail" ? C.red : C.muted;
    const label = status === "pass" ? "PASS" : status === "marginal" ? "MARGINAL" : status === "fail" ? "FAIL" : status?.toUpperCase() || "-";
    return (
      <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4, padding: "3px 10px", letterSpacing: 1 }}>{label}</span>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      <Card style={{ background: `${C.teal}06`, borderColor: `${C.teal}33`, padding: "12px 16px" }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.teal, lineHeight: 1.8 }}>
          <strong>EPA Plunger Lift Compliance Calculator</strong>: Enter well parameters to check:<br />
          <span style={{ color: C.muted }}>
            • GLR Feasibility [WP §4.1 Eq.1] · Shut-in Pressure Ratio [WP §4.3 Eq.4] · Load Factor [WP §4.2 Eq.3]<br />
            • Methane Emissions Estimate [EPA Natural Gas STAR: ER = W × (EFNP: EFP)]
          </span>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        {/* Input panel */}
        <Card>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 14 }}>Well Parameters</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CalcInput label="Well Depth" unit="ft" value={inputs.well_depth_ft} onChange={set("well_depth_ft")} hint="Used for GLR minimum calculation" placeholder="e.g. 4000-6000" />
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>PRESSURE INPUTS (psi), for Lf and Pressure Ratio</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <CalcInput label="Casing Pressure" unit="psi" value={inputs.casing_psi} onChange={set("casing_psi")} placeholder="e.g. 600-1200" />
                <CalcInput label="Tubing Pressure" unit="psi" value={inputs.tubing_psi} onChange={set("tubing_psi")} placeholder="e.g. 200-800" />
                <CalcInput label="Line Pressure" unit="psi" value={inputs.line_psi} onChange={set("line_psi")} placeholder="e.g. 100-300" />
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>GLR INPUTS, for GLR feasibility check</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <CalcInput label="Gas Rate" unit="MCF/d" value={inputs.gas_rate_mcfd} onChange={set("gas_rate_mcfd")} placeholder="e.g. 50-300" />
                <CalcInput label="Liquid Rate" unit="bbl/d" value={inputs.liquid_bbl} onChange={set("liquid_bbl")} placeholder="e.g. 5-50" />
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>EMISSIONS INPUTS, for CH₄ estimate</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <CalcInput label="Vent Duration" unit="min" value={inputs.vent_min} onChange={set("vent_min")} placeholder="e.g. 2-15" />
                <CalcInput label="Cycle Gas Vol" unit="MCF" value={inputs.vol_mcf} onChange={set("vol_mcf")} placeholder="e.g. 0.5-5" />
                <CalcInput label="Number of Wells" unit="wells" value={inputs.num_wells} onChange={set("num_wells")} hint="For fleet-level emissions" placeholder="e.g. 1-50" />
              </div>
            </div>
            <button
              onClick={calculate}
              disabled={loading}
              style={{
                background: loading ? "transparent" : `linear-gradient(135deg, ${C.cyan}22, ${C.teal}22)`,
                border: `1.5px solid ${loading ? C.border : C.cyan + "66"}`,
                borderRadius: 7, color: loading ? C.muted : C.cyan,
                fontFamily: MONO, fontSize: 11, padding: "10px 20px",
                cursor: loading ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {loading ? <><Icon name="clock" size={13} color={C.muted} /> Calculating…</> : <><Icon name="calculator" size={13} color={C.cyan} /> Calculate Compliance</>}
            </button>
            {error && <div style={{ fontFamily: MONO, fontSize: 9, color: C.red, background: `${C.red}0a`, border: `1px solid ${C.red}33`, borderRadius: 5, padding: "6px 10px" }}>Error: {error}</div>}
          </div>
        </Card>

        {/* Results panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {!result ? (
            <Card style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
              <div style={{ textAlign: "center", color: C.muted, fontFamily: MONO, fontSize: 11 }}>
                <Icon name="calculator" size={28} color={C.muted} />
                <div style={{ marginTop: 10 }}>Enter parameters and click Calculate</div>
              </div>
            </Card>
          ) : (
            <>
              {/* Verdict */}
              <Card accentTop={result.verdict?.overall === "Applicable" ? C.green : result.verdict?.overall === "Conditionally Applicable" ? C.amber : C.red}>
                <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Plunger Lift Applicability</div>
                <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 16, color: result.verdict?.overall === "Applicable" ? C.green : result.verdict?.overall === "Conditionally Applicable" ? C.amber : C.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                  {result.verdict?.overall}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {(result.verdict?.notes || []).map((n, i) => (
                    <div key={i} style={{ fontFamily: MONO, fontSize: 9, color: n.includes("✓") ? C.green : C.red }}>{n}</div>
                  ))}
                </div>
              </Card>

              {/* GLR */}
              <Card>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text }}>GLR Feasibility</div>
                  <StatusBadge status={result.glr?.status} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, marginBottom: 4 }}>{result.glr?.equation}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div style={{ background: C.panel, borderRadius: 5, padding: "6px 10px" }}>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>Minimum GLR</div>
                    <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: C.cyan }}>{fmt(result.glr?.minimum_scf_per_bbl, 0)} <span style={{ fontSize: 8 }}>scf/bbl</span></div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>at {fmt(result.glr?.well_depth_ft, 0)} ft depth</div>
                  </div>
                  <div style={{ background: C.panel, borderRadius: 5, padding: "6px 10px" }}>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>Actual GLR</div>
                    <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: result.glr?.status === "pass" ? C.green : C.orange }}>
                      {typeof result.glr?.actual_scf_per_bbl === "number" ? fmt(result.glr?.actual_scf_per_bbl, 0) + " scf/bbl" : "N/A (enter gas + liquid rates)"}
                    </div>
                  </div>
                </div>
                {result.glr?.note && <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 6 }}>{result.glr.note}</div>}
              </Card>

              {/* Pressure ratio, key is result.pressure_ratio (not shutin_pressure) */}
              <Card>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text }}>Shut-in Pressure Ratio</div>
                  <StatusBadge status={result.pressure_ratio?.status} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, marginBottom: 6 }}>{result.pressure_ratio?.equation}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    ["Casing Psi", result.pressure_ratio?.casing_psi != null ? fmt(result.pressure_ratio.casing_psi, 0) : "-", "psi"],
                    ["Line Psi",   result.pressure_ratio?.line_psi   != null ? fmt(result.pressure_ratio.line_psi,   0) : "-", "psi"],
                    ["Ratio",      result.pressure_ratio?.ratio       != null ? fmt(result.pressure_ratio.ratio,       3) : "-", `≥ ${result.pressure_ratio?.minimum_ratio ?? 1.5}×`],
                  ].map(([label, val, unit]) => (
                    <div key={label} style={{ background: C.panel, borderRadius: 5, padding: "6px 10px" }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{label}</div>
                      <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: C.cyan }}>{val}</div>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{unit}</div>
                    </div>
                  ))}
                </div>
                {result.pressure_ratio?.note && <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 6 }}>{result.pressure_ratio.note}</div>}
              </Card>
              <Card>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text }}>Load Factor</div>
                  <StatusBadge status={result.load_factor?.status} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, marginBottom: 6 }}>{result.load_factor?.equation}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 6 }}>
                  {[
                    ["Casing", result.load_factor?.casing_psi != null ? fmt(result.load_factor.casing_psi, 0) : "-", "psi"],
                    ["Tubing", result.load_factor?.tubing_psi != null ? fmt(result.load_factor.tubing_psi, 0) : "-", "psi"],
                    ["Line",   result.load_factor?.line_psi   != null ? fmt(result.load_factor.line_psi,   0) : "-", "psi"],
                  ].map(([label, val, unit]) => (
                    <div key={label} style={{ background: C.panel, borderRadius: 5, padding: "6px 10px" }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{label}</div>
                      <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>{val} <span style={{ fontSize: 8, color: C.muted }}>{unit}</span></div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ background: C.panel, borderRadius: 5, padding: "6px 14px" }}>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>Calculated Lf</div>
                    <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: result.load_factor?.status === "pass" ? C.green : result.load_factor?.status === "marginal" ? C.amber : result.load_factor?.status === "fail" ? C.red : C.muted }}>
                      {result.load_factor?.load_factor != null ? fmt(result.load_factor.load_factor, 4) : "N/A"}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
                      fail ≥ {result.load_factor?.fail_threshold ?? 0.5}  ·  warn ≥ {result.load_factor?.warn_threshold ?? 0.4}
                    </div>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, flex: 1, lineHeight: 1.6 }}>{result.load_factor?.note}</div>
                </div>
              </Card>
              <Card>
                <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text, marginBottom: 6 }}>Methane Emissions Estimate</div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: `${EPA.emit}99`, marginBottom: 8 }}>{result.emissions?.formula}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    ["Per Cycle CH₄",  result.emissions?.per_cycle?.ch4_kg  != null ? fmt(result.emissions.per_cycle.ch4_kg,  4) : "-", "kg"],
                    ["Per Cycle CO₂e", result.emissions?.per_cycle?.co2e_kg != null ? fmt(result.emissions.per_cycle.co2e_kg, 4) : "-", "kg"],
                    ["Annual CH₄ (1 well)", result.emissions?.annual_single_well?.ch4_kg != null ? fmtK(result.emissions.annual_single_well.ch4_kg) : "-", "kg/yr"],
                    [`Fleet CH₄ (${result.emissions?.fleet?.num_wells ?? inputs.num_wells} wells)`, result.emissions?.fleet?.ch4_kg != null ? fmtK(result.emissions.fleet.ch4_kg) : "-", "kg/yr"],
                    ["Annual CO₂e (1 well)", result.emissions?.annual_single_well?.co2e_kg != null ? fmtK(result.emissions.annual_single_well.co2e_kg) : "-", "kg CO₂e/yr"],
                    ["Reduction vs No-Plunger", result.emissions?.reduction_estimate?.annual_reduction_single_kg != null ? fmtK(result.emissions.reduction_estimate.annual_reduction_single_kg) : "-", "kg CH₄/yr saved"],
                  ].map(([label, val, unit]) => (
                    <div key={label} style={{ background: C.panel, borderRadius: 5, padding: "6px 10px" }}>
                      <div style={{ fontFamily: MONO, fontSize: 7, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                      <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: EPA.emit }}>{val} <span style={{ fontSize: 8 }}>{unit}</span></div>
                    </div>
                  ))}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 8, lineHeight: 1.7 }}>
                  CH₄ density: {result.emissions?.ch4_density_kg_per_mcf ?? 19.08} kg/MCF · GWP-100: {result.emissions?.co2e_factor ?? 25}× [IPCC AR5]
                  {result.emissions?.reduction_estimate?.note && <><br />{result.emissions.reduction_estimate.note}</>}
                </div>
              </Card>
              <Card style={{
                background: result.ai_recommendation?.status === "critical" ? `${C.red}0a`
                          : result.ai_recommendation?.status === "warning"  ? `${C.amber}0a`
                          : `${C.cyan}08`,
                borderColor: result.ai_recommendation?.status === "critical" ? `${C.red}44`
                           : result.ai_recommendation?.status === "warning"  ? `${C.amber}44`
                           : `${C.cyan}33`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Icon
                    name={result.ai_recommendation?.status === "critical" ? "alert-circle"
                        : result.ai_recommendation?.status === "warning"  ? "alert-triangle"
                        : "bot"}
                    size={15}
                    color={result.ai_recommendation?.status === "critical" ? C.red
                         : result.ai_recommendation?.status === "warning"  ? C.amber
                         : C.cyan}
                  />
                  <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 11, color: C.text }}>
                    AI Recommendation
                  </div>
                </div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.text, lineHeight: 1.7 }}>
                  {result.ai_recommendation?.text ?? "Recommendation not available."}
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 11. Reports Tab 
function downloadJSON(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob); const a = document.createElement("a");
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
function arrayToCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  return [headers.join(","), ...rows.map(r => headers.map(h => { const v = r[h]; if (v == null) return ""; const s = String(v); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; }).join(","))].join("\n");
}
function downloadCSV(rows, filename) {
  const blob = new Blob([arrayToCSV(rows)], { type: "text/csv" }); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
// Flattens a nested report object (like /overview) into Metric/Value rows so it can be exported as CSV too.
function flattenToRows(obj, prefix = "") {
  const rows = [];
  for (const [key, val] of Object.entries(obj || {})) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === "object" && !Array.isArray(val)) {
      rows.push(...flattenToRows(val, label));
    } else if (Array.isArray(val)) {
      if (val.length && typeof val[0] === "object" && val[0] !== null) {
        val.forEach((item, i) => rows.push(...flattenToRows(item, `${label}[${i}]`)));
      } else {
        rows.push({ Metric: label, Value: val.join("; ") });
      }
    } else {
      rows.push({ Metric: label, Value: val });
    }
  }
  return rows;
}
function StructuredPreview({ data, reportId, csvKey, csvBuilder, color }) {
  if (!data) return <div style={{ color: C.muted, fontFamily: MONO, padding: 20 }}>No data</div>;

  const isArray = Array.isArray(data);
  const displayData = csvKey && data[csvKey] ? data[csvKey] : (csvBuilder ? csvBuilder(data) : data);

  if (isArray || Array.isArray(displayData)) {
    const rows = Array.isArray(displayData) ? displayData : [];
    if (rows.length === 0) {
      return <pre style={{ background: C.panel, padding: 16, borderRadius: 8, fontSize: 10, color: C.muted, overflow: "auto", maxHeight: 400 }}>
        {JSON.stringify(data, null, 2)}
      </pre>;
    }

    const headers = Object.keys(rows[0] || {});

    return (
      <div style={{ overflowX: "auto", maxHeight: 420, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 9 }}>
          <thead>
            <tr style={{ background: `${color}15`, position: "sticky", top: 0, zIndex: 1 }}>
              {headers.map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${C.border}`, color: color, whiteSpace: "nowrap" }}>
                  {h.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((row, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                {headers.map(h => {
                  const val = row[h];
                  let display = val;
                  if (typeof val === "number") display = val.toFixed(Math.abs(val) < 1 ? 3 : 1);
                  else if (Array.isArray(val)) display = val.join(", ");
                  return (
                    <td key={h} style={{ padding: "7px 10px", color: typeof val === "number" ? C.cyan : C.text }}>
                      {String(display ?? "-").slice(0, 60)}{String(display ?? "").length > 60 ? "..." : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 50 && (
          <div style={{ padding: "8px 12px", fontFamily: MONO, fontSize: 9, color: C.muted, textAlign: "center", background: C.panel }}>
            Showing first 50 of {rows.length} rows • Full data available in JSON/CSV download
          </div>
        )}
      </div>
    );
  }

  return (
    <pre style={{
      background: C.panel, padding: 16, borderRadius: 8,
      fontSize: 10, color: C.text, overflow: "auto", maxHeight: 500,
      border: `1px solid ${C.border2}`
    }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
function TabReports({ well }) {
  const [loadingBtn, setLoadingBtn]   = useState(null);   
  const [status, setStatus]           = useState({});
  const [previews, setPreviews]       = useState({});     
  const [openPreview, setOpenPreview] = useState(null);   
  const wellParam = well && well !== "all" ? `?well=${encodeURIComponent(well)}` : "";

  async function fetchData(endpoint) {
    const body = (await axios.get(`${EPA_API}${endpoint}${wellParam}`)).data;
    return body;
  }

  async function handleJSON(r) {
    const btnKey = `${r.id}_json`;
    setLoadingBtn(btnKey);
    setStatus(s => ({ ...s, [r.id]: "Fetching…" }));
    try {
      const body = await fetchData(r.endpoint);
      downloadJSON(body, r.jsonFile);
      setPreviews(p => ({ ...p, [r.id]: { label: r.label, data: body } }));
      setOpenPreview(r.id);
      setStatus(s => ({ ...s, [r.id]: "✓ JSON downloaded" }));
    } catch (e) {
      setStatus(s => ({ ...s, [r.id]: `Error: ${e.message}` }));
    }
    setLoadingBtn(null);
  }

  async function handleCSV(r) {
    const btnKey = `${r.id}_csv`;
    setLoadingBtn(btnKey);
    setStatus(s => ({ ...s, [r.id]: "Fetching…" }));
    try {
      const body = await fetchData(r.endpoint);
      const rows = r.csvBuilder ? r.csvBuilder(body) : (r.csvKey ? body[r.csvKey] : null);
      if (rows?.length) {
        downloadCSV(rows, r.csvFile);
        setStatus(s => ({ ...s, [r.id]: "✓ CSV downloaded" }));
      } else {
        setStatus(s => ({ ...s, [r.id]: "No CSV rows available" }));
      }
      setPreviews(p => ({ ...p, [r.id]: { label: r.label, data: body } }));
      setOpenPreview(r.id);
    } catch (e) {
      setStatus(s => ({ ...s, [r.id]: `Error: ${e.message}` }));
    }
    setLoadingBtn(null);
  }

  async function handlePreview(r) {
    setLoadingBtn(`${r.id}_preview`);
    try {
      const body = await fetchData(r.endpoint);
      setPreviews(p => ({ ...p, [r.id]: { label: r.label, data: body } }));
      setOpenPreview(r.id);
    } catch (e) {
      setStatus(s => ({ ...s, [r.id]: `Preview error: ${e.message}` }));
    }
    setLoadingBtn(null);
  }

  const REPORTS = [
    { id: "full_audit",          label: "Full Cycle Audit Report",        color: C.cyan,   icon: "clipboard", endpoint: "/export-data",         jsonFile: "epa_full_cycle_audit.json",          csvKey: "data",       csvFile: "epa_full_cycle_audit.csv",          columns: "Cycle · Date · Velocity · Load Factor · Shut-In Eff · Vent · CH₄ · CO₂e · Score · Tier · Flags · Clean" },
    { id: "scorecard",           label: "EPA Compliance Scorecard",       color: C.teal,   icon: "award",     endpoint: "/scorecard",           jsonFile: "epa_compliance_scorecard.json",       csvKey: "components", csvFile: "epa_compliance_scorecard.csv",        columns: "Component · Current Score · Previous Score · Δ Delta · Overall · Tier" },
    { id: "venting",             label: "Venting Risk Report",            color: C.orange, icon: "wind",      endpoint: "/venting-risk",        jsonFile: "epa_venting_risk.json",               csvKey: "trend",      csvFile: "epa_venting_risk.csv",                columns: "Cycle · Date · Risk Score · Rolling Avg · CH₄ · Load Factor · Vent" },
    { id: "asset_ranking",       label: "Asset Ranking Report",           color: C.amber,  icon: "bar-chart", endpoint: "/asset-ranking",       jsonFile: "epa_asset_ranking.json",              csvKey: "rankings",   csvFile: "epa_asset_ranking.csv",               columns: "Rank · Well · Score · Tier · Risk · Cycles · Clean % · CH₄ · Velocity · Load Factor" },
    { id: "emissions_inventory", label: "Emissions Inventory Report",     color: EPA.emit, icon: "globe",     endpoint: "/emissions-inventory", jsonFile: "epa_emissions_inventory.json",        csvKey: "monthly",    csvFile: "epa_emissions_inventory_monthly.csv", columns: "Period · CH₄ (kg) · CO₂e (kg) · Cycles · Vent Events · Avg Vent" },
    { id: "timeline",            label: "Regulatory Compliance Timeline", color: C.pink,   icon: "calendar",  endpoint: "/timeline",            jsonFile: "epa_compliance_timeline.json",        csvKey: "timeline",   csvFile: "epa_compliance_timeline.csv",         columns: "Month · Avg Score · Tier · Tier Short · Cycles · Status Change Event" },
    { id: "overview",            label: "Executive Overview Export",      color: C.blue,   icon: "shield",    endpoint: "/overview",            jsonFile: "epa_executive_overview.json",         csvKey: null,         csvFile: "epa_executive_overview.csv",          csvBuilder: flattenToRows, columns: "Score · Tier · CH₄ · CO₂e · Clean % · Fleet Risk · All 8 Thresholds" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionTitle accent={C.teal}>Reports &amp; Regulatory Exports</SectionTitle>
      <Card style={{ padding: "10px 16px", background: `${C.amber}0a`, borderColor: `${C.amber}33` }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.amber, lineHeight: 1.6, display: "flex", alignItems: "flex-start", gap: 7 }}>
          <Icon name="alert-triangle" size={13} color={C.amber} />
          <span>Each report has separate <strong>JSON</strong> and <strong>CSV</strong> download buttons. Click <strong>Preview</strong> to inspect data before downloading.</span>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
        {REPORTS.map(r => (
          <Card key={r.id} accentTop={r.color} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Title row */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Icon name={r.icon} size={18} color={r.color} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color: C.text }}>{r.label}</div>
              </div>
            </div>

            {/* Columns preview strip */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 5, padding: "6px 10px", fontFamily: MONO, fontSize: 8, color: `${r.color}99`, lineHeight: 1.6 }}>
              <span style={{ color: C.muted, letterSpacing: 1, textTransform: "uppercase", fontSize: 7 }}>Columns: </span>{r.columns}
            </div>

            {/* action buttons: JSON · CSV · Preview */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {/* JSON download */}
              <button
                disabled={loadingBtn === `${r.id}_json`}
                onClick={() => handleJSON(r)}
                style={{ background: `${r.color}18`, border: `1.5px solid ${r.color}66`, borderRadius: 6, color: r.color, fontFamily: MONO, fontSize: 9, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, opacity: loadingBtn === `${r.id}_json` ? 0.5 : 1 }}
              >
                <Icon name="download" size={10} color={r.color} /> JSON
              </button>

              {/* CSV download, disabled if no csvKey/csvBuilder */}
              {(r.csvKey || r.csvBuilder) ? (
                <button
                  disabled={loadingBtn === `${r.id}_csv`}
                  onClick={() => handleCSV(r)}
                  style={{ background: `${C.green}12`, border: `1.5px solid ${C.green}55`, borderRadius: 6, color: C.green, fontFamily: MONO, fontSize: 9, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, opacity: loadingBtn === `${r.id}_csv` ? 0.5 : 1 }}
                >
                  <Icon name="download" size={10} color={C.green} /> CSV
                </button>
              ) : (
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, padding: "5px 6px" }}>CSV N/A</span>
              )}

              {/* Preview button */}
              <button
                disabled={loadingBtn === `${r.id}_preview`}
                onClick={() => {
                  if (openPreview === r.id) { setOpenPreview(null); }
                  else { handlePreview(r); }
                }}
                style={{ background: openPreview === r.id ? `${C.cyan}22` : "transparent", border: `1.5px solid ${openPreview === r.id ? C.cyan : C.border}`, borderRadius: 6, color: openPreview === r.id ? C.cyan : C.muted, fontFamily: MONO, fontSize: 9, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <Icon name="search" size={10} color={openPreview === r.id ? C.cyan : C.muted} />
                {openPreview === r.id ? "Hide" : "Preview"}
              </button>

              {/* Status */}
              {status[r.id] && (
                <span style={{ fontFamily: MONO, fontSize: 8, color: status[r.id].includes("Error") || status[r.id].includes("error") ? C.red : C.green }}>
                  {status[r.id]}
                </span>
              )}
            </div>

            {/* Inline preview, shown only for this card */}
            {openPreview === r.id && previews[r.id] && (
              <div style={{ marginTop: 4 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontFamily: MONO, fontSize: 8, color: C.cyan, letterSpacing: 0.5 }}>DATA PREVIEW, {previews[r.id].label}</div>
                  <button onClick={() => setOpenPreview(null)} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", padding: 0 }}><Icon name="x" size={12} color={C.muted} /></button>
                </div>
                <StructuredPreview data={previews[r.id].data} reportId={r.id} csvKey={r.csvKey} csvBuilder={r.csvBuilder} color={r.color} />
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Download All */}
      <Card style={{ textAlign: "center", padding: "16px 24px" }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 4 }}>Download All Reports</div>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginBottom: 14 }}>Downloads all 7 reports: JSON + CSV where available.</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            disabled={loadingBtn === "all_json"}
            onClick={async () => {
              setLoadingBtn("all_json");
              for (const r of REPORTS) { await handleJSON(r); await new Promise(res => setTimeout(res, 300)); }
              setLoadingBtn(null);
            }}
            style={{ background: `${C.cyan}18`, border: `1.5px solid ${C.cyan}55`, borderRadius: 7, color: C.cyan, fontFamily: MONO, fontSize: 10, padding: "9px 22px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: loadingBtn === "all_json" ? 0.5 : 1 }}
          >
            <Icon name="download" size={12} color={C.cyan} />
            {loadingBtn === "all_json" ? "Downloading JSON…" : "Download All JSON"}
          </button>
          <button
            disabled={loadingBtn === "all_csv"}
            onClick={async () => {
              setLoadingBtn("all_csv");
              for (const r of REPORTS.filter(x => x.csvKey || x.csvBuilder)) { await handleCSV(r); await new Promise(res => setTimeout(res, 300)); }
              setLoadingBtn(null);
            }}
            style={{ background: `${C.green}12`, border: `1.5px solid ${C.green}44`, borderRadius: 7, color: C.green, fontFamily: MONO, fontSize: 10, padding: "9px 22px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: loadingBtn === "all_csv" ? 0.5 : 1 }}
          >
            <Icon name="download" size={12} color={C.green} />
            {loadingBtn === "all_csv" ? "Downloading CSV…" : "Download All CSV"}
          </button>
        </div>
      </Card>
    </div>
  );
}

const EPA_TABS = [
  { id: "overview",      label: "Overview",      icon: "shield"    },
  { id: "applicability", label: "Applicability", icon: "clipboard" },
  { id: "audit",         label: "Cycle Audit",   icon: "search"    },
  { id: "plots",         label: "Cycle Plots",   icon: "activity"  },
  { id: "venting",       label: "Venting Risk",  icon: "wind"      },
  { id: "scorecard",     label: "Scorecard",     icon: "award"     },
  { id: "ranking",       label: "Asset Ranking", icon: "bar-chart" },
  { id: "emissions",     label: "Emissions",     icon: "globe"     },
  { id: "timeline",      label: "Timeline",      icon: "calendar"  },
  { id: "calculator",    label: "Calculator",    icon: "calculator"},
  { id: "reports",       label: "Reports",       icon: "download"  },
];

export default function EPACompliancePage() {
  const { selectedWell } = useWell();
  const [activeTab, setActiveTab] = useState("overview");

  /* DATA WINDOW: every cycle's open time (UNFILTERED) -> presets; the window is sent to the EPA service as from/to/last */
  const [cycleTimes, setCycleTimes] = useState([]);
  useEffect(() => {
    const wp = selectedWell && selectedWell !== "all" ? `?well=${encodeURIComponent(selectedWell)}` : "";
    axios.get(`${EPA_API}/cycle-times${wp}`)
      .then(r => setCycleTimes((r.data?.cycles || []).map(c => ({ well: c.well, time: c.time, _t: parseT(c.time) }))))
      .catch(() => setCycleTimes([]));
  }, [selectedWell]);
  const tw = useTimeWindow(cycleTimes);
  const winQs = useMemo(() => epaWinQs(tw.win, tw.tMax), [tw.win, tw.tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const windowed = !["calculator", "reports"].includes(activeTab);              // calculator = your own inputs, reports = full export

  const { isDark } = useTheme();       
  applyTheme(getColors(isDark));        

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Page header, clean, no giant score banner */}
      <div style={{
        background: `linear-gradient(135deg, ${C.green}08 0%, ${C.panel} 50%)`,
        border: `1px solid ${C.border}`, borderRadius: "12px 12px 0 0",
        padding: "14px 18px 0", marginBottom: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: `${C.green}18`, border: `2px solid ${C.green}55`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Icon name="leaf" size={16} color={C.green} />
          </div>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 1 }}>
            EPA Natural Gas STAR . NSPS OOOO/OOOOa · GWP-100 (IPCC AR5)
            </div>
          </div>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", paddingBottom: 4 }}>
          {EPA_TABS.map(t => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: "7px 16px", borderRadius: 8,
                  border: `1px solid ${active ? C.green : C.border2}`,
                  background: active ? `${C.green}18` : C.panel,
                  color: active ? C.green : C.muted,
                  fontFamily: MONO, fontSize: 9, fontWeight: 700,
                  letterSpacing: 1, textTransform: "uppercase",
                  cursor: "pointer", whiteSpace: "nowrap",
                  display: "flex", alignItems: "center", gap: 7,
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.color = C.text; e.currentTarget.style.borderColor = C.border; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = C.border2; } }}
              >
                <Icon name={t.icon} size={12} color={active ? C.green : C.muted} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderTop: "none", borderRadius: "0 0 12px 12px",
        padding: "20px",
        minHeight: 500,
      }}>
        {windowed && cycleTimes.some(c => c._t != null) && (
          <div style={{ marginBottom: 18 }}>
            <DataWindowBar ctl={tw} total={cycleTimes.length} isDark={isDark}
              note={`The window applies to every EPA tab except Calculator and Reports (Reports always export the full data). Compliance thresholds stay fleet-wide.${selectedWell === "all" ? " With All Wells selected, \"LAST 30 CYC\" means the newest 30 cycles of each well." : ""}`}/>
          </div>
        )}
        {activeTab === "overview"      && <TabOverview      well={selectedWell} winQs={winQs} />}
        {activeTab === "applicability" && <TabApplicability well={selectedWell} winQs={winQs} />}
        {activeTab === "audit"         && <TabCycleAudit    well={selectedWell} winQs={winQs} />}
        {activeTab === "plots"         && <TabCyclePlots    well={selectedWell} winQs={winQs} />}
        {activeTab === "venting"       && <TabVentingRisk   well={selectedWell} winQs={winQs} />}
        {activeTab === "scorecard"     && <TabScorecard     well={selectedWell} winQs={winQs} />}
        {activeTab === "ranking"       && <TabAssetRanking winQs={winQs} />}
        {activeTab === "emissions"     && <TabEmissions     well={selectedWell} winQs={winQs} />}
        {activeTab === "timeline"      && <TabTimeline      well={selectedWell} winQs={winQs} />}
        {activeTab === "calculator"    && <TabCalculator />}
        {activeTab === "reports"       && <TabReports       well={selectedWell} />}
      </div>
    </div>
  );
}